<template>
  <div class="birdseye-page-container">
    <!-- Plant Information Card -->
    <div class="plant-info-card">
      <div class="plant-info-header">
        <div class="plant-info-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          <h4>Plant Information</h4>
        </div>
      </div>
      <div class="plant-info-grid plant-details-grid">
        <div class="info-item">
          <div class="info-label">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            <span>Alias</span>
          </div>
          <div class="info-value">{{ alias || 'N/A' }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <span>Plant ID</span>
          </div>
          <div class="info-value">{{ plantId }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span>Time Zone</span>
          </div>
          <div class="info-value">{{ timezoneDataMap.timezone_name || 'N/A' }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span>Local Time</span>
          </div>
          <div class="info-value">
            <LocalTime timeZoneName="Australia/Brisbane" :offset="timezoneDataMap.timezone_offset" />
          </div>
        </div>
        <div class="info-item">
          <div class="info-label">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <span>Date Added</span>
          </div>
          <div class="info-value">{{ formattedDateAdded }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9z"/>
              <path d="M3 9l2.45-4.9A2 2 0 0 1 7.24 3h9.52a2 2 0 0 1 1.79 1.1L21 9"/>
              <path d="M12 3v6"/>
            </svg>
            <span>Retailer</span>
          </div>
          <div class="info-value">{{ retailerName || 'N/A' }}</div>
        </div>
      </div>
    </div>

    <div class="plant-info-card data-availability-card">
      <div class="plant-info-header">
        <div class="plant-info-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 3v18h18"/>
            <path d="M7 15l3-3 3 2 4-5"/>
          </svg>
          <h4>Data Availability</h4>
        </div>
      </div>
      <div v-if="dataAvailabilityLoading" class="data-availability-status">
        Checking data ranges...
      </div>
      <div v-else-if="dataAvailabilityError" class="data-availability-status error">
        {{ dataAvailabilityError }}
      </div>
      <div v-else class="plant-info-grid">
        <div class="info-item">
          <div class="info-label">
            <span>PV Prediction</span>
          </div>
          <div class="info-value">{{ dataAvailability.pv.startDate }} - {{ dataAvailability.pv.endDate }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">
            <span>Load Prediction</span>
          </div>
          <div class="info-value">{{ dataAvailability.load.startDate }} - {{ dataAvailability.load.endDate }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">
            <span>Price Prediction</span>
          </div>
          <div class="info-value">{{ dataAvailability.price.startDate }} - {{ dataAvailability.price.endDate }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">
            <span>Renox Actual Data</span>
          </div>
          <div class="info-value">{{ dataAvailability.renox.startDate }} - {{ dataAvailability.renox.endDate }}</div>
        </div>
      </div>
    </div>
    
    <!-- Quick Actions Card -->
    <div class="quick-actions-card" v-if="accessLevelNumber == 10 || accessLevelNumber == 2">
      <div class="quick-actions-header">
        <h4>Quick Actions</h4>
      </div>
      <div class="quick-actions-grid">
        <button 
          class="action-button" 
          :class="{ active: isEvalEnabled }"
          @click="togglePredictionEvaluation"
        >
          <div class="action-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>
          <div class="action-content">
            <span class="action-title">Prediction Evaluation</span>
            <span class="action-status">{{ isEvalEnabled ? 'Visible' : 'Hidden' }}</span>
          </div>
        </button>
        
        <button 
          class="action-button" 
          :class="{ active: isPlantDevicesDataBoxVisible }"
          @click="isPlantDevicesDataBoxVisible ? isPlantDevicesDataBoxVisible = false : loadAndShowPlantDevices()"
        >
          <div class="action-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
              <line x1="8" y1="21" x2="16" y2="21"/>
              <line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
          </div>
          <div class="action-content">
            <span class="action-title">Plant Devices</span>
            <span class="action-status">{{ isPlantDevicesDataBoxVisible ? 'Visible' : 'Hidden' }}</span>
          </div>
        </button>
        
        <button 
          class="action-button" 
          :class="{ active: isUserPreferencesEnabled }"
          @click="isUserPreferencesEnabled = !isUserPreferencesEnabled"
        >
          <div class="action-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          </div>
          <div class="action-content">
            <span class="action-title">Display Preferences</span>
            <span class="action-status">{{ isUserPreferencesEnabled ? 'Visible' : 'Hidden' }}</span>
          </div>
        </button>
      </div>
    </div>
    
    <!-- Display Preferences Container -->
    <div v-if="isUserPreferencesEnabled" class="user-preferences-container">
      <h6>Display Preferences</h6>
      <div class="preferences-grid">
        <div class="preference-item">
          <input 
            type="checkbox" 
            id="predictedBatteryActions"
            v-model="userPreferences.predictedBatteryActions"
            @change="saveUserPreferences"
            class="preference-checkbox"
          />
          <label for="predictedBatteryActions" class="preference-label">
            Predicted Battery Actions
          </label>
        </div>

        <div class="preference-item">
          <input 
            type="checkbox" 
            id="predictedBatteryActionsOracle"
            v-model="userPreferences.predictedBatteryActionsOracle"
            @change="saveUserPreferences"
            class="preference-checkbox"
          />
          <label for="predictedBatteryActionsOracle" class="preference-label">
            Oracle Battery Actions
          </label>
        </div>

        <div class="preference-item">
          <input 
            type="checkbox" 
            id="pricePrediction"
            v-model="userPreferences.pricePrediction"
            @change="saveUserPreferences"
            class="preference-checkbox"
          />
          <label for="pricePrediction" class="preference-label">
            Price Prediction
          </label>
        </div>

        <div class="preference-item">
          <input 
            type="checkbox" 
            id="pvPredictionNonAdjusted"
            v-model="userPreferences.pvPredictionNonAdjusted"
            @change="saveUserPreferences"
            class="preference-checkbox"
          />
          <label for="pvPredictionNonAdjusted" class="preference-label">
            PV Prediction (non-adjusted)
          </label>
        </div>

        <div class="preference-item">
          <input 
            type="checkbox" 
            id="loadPredictionLowerBound"
            v-model="userPreferences.loadPredictionLowerBound"
            @change="saveUserPreferences"
            class="preference-checkbox"
          />
          <label for="loadPredictionLowerBound" class="preference-label">
            Load Prediction Lower Bound
          </label>
        </div>

        <div class="preference-item">
          <input 
            type="checkbox" 
            id="loadPredictionUpperBound"
            v-model="userPreferences.loadPredictionUpperBound"
            @change="saveUserPreferences"
            class="preference-checkbox"
          />
          <label for="loadPredictionUpperBound" class="preference-label">
            Load Prediction Upper Bound
          </label>
        </div>

        <div class="preference-item">
          <input 
            type="checkbox" 
            id="predictedLoadHourly"
            v-model="userPreferences.predictedLoadHourly"
            @change="saveUserPreferences"
            class="preference-checkbox"
          />
          <label for="predictedLoadHourly" class="preference-label">
            Predicted Load Hourly
          </label>
        </div>

        <div class="preference-item">
          <input 
            type="checkbox" 
            id="priceActualBuySellRX"
            v-model="userPreferences.priceActualBuySellRX"
            @change="saveUserPreferences"
            class="preference-checkbox"
          />
          <label for="priceActualBuySellRX" class="preference-label">
            Price Actual Buy/Sell (RX)
          </label>
        </div>

        <div class="preference-item">
          <input 
            type="checkbox" 
            id="predictedGridPower"
            v-model="userPreferences.predictedGridPower"
            @change="saveUserPreferences"
            class="preference-checkbox"
          />
          <label for="predictedGridPower" class="preference-label">
            Predicted Grid Power
          </label>
        </div>

        <div class="preference-item">
          <input 
            type="checkbox" 
            id="actionReasons"
            v-model="userPreferences.actionReasons"
            @change="saveUserPreferences"
            class="preference-checkbox"
          />
          <label for="actionReasons" class="preference-label">
            Action Reasons
          </label>
        </div>

        <div class="preference-item">
          <input 
            type="checkbox" 
            id="bmsSimulator"
            v-model="userPreferences.bmsSimulator"
            @change="saveUserPreferences"
            class="preference-checkbox"
          />
          <label for="bmsSimulator" class="preference-label">
            BMS Simulator
          </label>
        </div>

        <div class="preference-item">
          <input 
            type="checkbox" 
            id="showSeparateSocChart"
            v-model="userPreferences.showSeparateSocChart"
            @change="saveUserPreferences"
            class="preference-checkbox"
          />
          <label for="showSeparateSocChart" class="preference-label">
            Separate SoC Chart
          </label>
        </div>

        <div class="preference-item">
          <input 
            type="checkbox" 
            id="showDeviceSoHComparison"
            v-model="userPreferences.showDeviceSoHComparison"
            @change="saveUserPreferences"
            class="preference-checkbox"
          />
          <label for="showDeviceSoHComparison" class="preference-label">
            Show device SoH comparison (AI vs actual)
          </label>
        </div>

        <div class="preference-item">
          <input 
            type="checkbox" 
            id="useLegacyDesign"
            :checked="isLegacyDesign"
            @change="handleLegacyDesignToggle"
            class="preference-checkbox"
          />
          <label for="useLegacyDesign" class="preference-label">
            Use Legacy Design
          </label>
        </div>
      </div>
      
      <div class="preferences-actions">
        <button @click="resetPreferences" class="custom-button reset-button">
          Reset to Defaults
        </button>
      </div>
    </div>
    <br>
    
    <!-- Plant Devices Display -->
    <div v-if="isPlantDevicesDataBoxVisible" class="plant-devices-card">
      <div class="devices-header">
        <div class="devices-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
            <line x1="8" y1="21" x2="16" y2="21"/>
            <line x1="12" y1="17" x2="12" y2="21"/>
          </svg>
          <h4>Plant Devices</h4>
        </div>
        <div class="devices-count-badge" v-if="plantDevicesDataMap.length > 0">
          <span>{{ plantDevicesDataMap.length }}</span>
          <span class="count-label">{{ plantDevicesDataMap.length === 1 ? 'Device' : 'Devices' }}</span>
        </div>
      </div>
      
      <div v-if="plantDevicesDataMap.length === 0" class="no-devices-message">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <p>No devices found under this plant.</p>
      </div>
      
      <div v-else class="devices-grid">
        <div 
          v-for="(device, index) in plantDevicesDataMap" 
          :key="index" 
          class="device-card"
        >
          <div class="device-card-header">
            <div class="device-number">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                <line x1="8" y1="21" x2="16" y2="21"/>
                <line x1="12" y1="17" x2="12" y2="21"/>
              </svg>
              <span>Device {{ index + 1 }}</span>
            </div>
          </div>
          
          <div class="device-card-body">
            <div class="device-field">
              <div class="device-field-label">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <span>Serial Number</span>
              </div>
              <a 
                :href="'https://redxpower.com/#/device/detail/' + device.device_sn" 
                target="_blank" 
                class="device-sn-link"
              >
                {{ device.device_sn }}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15 3 21 3 21 9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </a>
            </div>
            
            <div class="device-field">
              <div class="device-field-label">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
                <span>Device Type</span>
              </div>
              <div class="device-type-badge">
                {{ device.device_type || 'N/A' }}
              </div>
            </div>

            <div v-if="userPreferences.showDeviceSoHComparison" class="device-soh-comparison">
              <div class="device-field-label soh-comparison-title">State of Health comparison</div>
              <div v-if="deviceSoHComparisonMap[device.device_sn]?.loading" class="device-soh-loading">
                Loading SoH…
              </div>
              <template v-else>
                <div class="device-field">
                  <div class="device-field-label">AI (estimated)</div>
                  <div class="device-soh-values">
                    <span>SoH: {{ deviceSoHComparisonMap[device.device_sn]?.ai?.device_soh != null ? (deviceSoHComparisonMap[device.device_sn].ai.device_soh * 100).toFixed(1) + '%' : 'N/A' }}</span>
                    <span>RTE: {{ deviceSoHComparisonMap[device.device_sn]?.ai?.device_rte != null ? (deviceSoHComparisonMap[device.device_sn].ai.device_rte * 100).toFixed(1) + '%' : 'N/A' }}</span>
                  </div>
                </div>
                <div class="device-field">
                  <div class="device-field-label">Actual (Renox)</div>
                  <div class="device-soh-values">
                    <span>SoH: {{ deviceSoHComparisonMap[device.device_sn]?.rx?.bmsSoh != null ? deviceSoHComparisonMap[device.device_sn].rx.bmsSoh + '%' : 'N/A' }}</span>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Date Range Selection with Professional Tabs -->
    <div class="date-range-selector-wrapper">
      <div class="date-range-tabs-container">
        <div class="tabs-header">
          <button
            v-for="option in dateRangeOptions"
            :key="option.value"
            :class="{ active: selectedDateRange === option.value }"
            @click="selectDateRange(option.value)"
            class="tab-button"
          >
            {{ option.label }}
          </button>
        </div>
        <div class="tab-content-panel">
          <!-- Weekly Range Type Toggle (only for weekly tab) - moved to top -->
          <div v-if="selectedDateRange === 'weekly'" class="monthly-options">
            <div class="monthly-toggle-container">
              <label class="monthly-toggle-label">Range Type:</label>
              <div class="monthly-toggle-switch">
                <button
                  class="monthly-toggle-btn"
                  :class="{ active: weeklyRangeType === '7days' }"
                  @click="setWeeklyRangeType('7days')"
                >
                  7 Days Range
                </button>
                <button
                  class="monthly-toggle-btn"
                  :class="{ active: weeklyRangeType === 'calendar' }"
                  @click="setWeeklyRangeType('calendar')"
                >
                  Calendar Week
                </button>
              </div>
            </div>
            
            <!-- Week Start Date Picker for Calendar Week -->
            <div v-if="weeklyRangeType === 'calendar'" class="month-year-picker">
              <div class="month-year-inputs">
                <div class="month-year-group">
                  <label>Week Start (Monday):</label>
                  <input 
                    type="date" 
                    v-model="selectedWeekStartDate" 
                    @change="onWeekStartDateChange" 
                    class="week-start-input"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <!-- Monthly Range Type Toggle (only for monthly tab) - moved to top -->
          <div v-if="selectedDateRange === 'monthly'" class="monthly-options">
            <div class="monthly-toggle-container">
              <label class="monthly-toggle-label">Range Type:</label>
              <div class="monthly-toggle-switch">
                <button
                  class="monthly-toggle-btn"
                  :class="{ active: monthlyRangeType === '30days' }"
                  @click="setMonthlyRangeType('30days')"
                >
                  30 Days Range
                </button>
                <button
                  class="monthly-toggle-btn"
                  :class="{ active: monthlyRangeType === 'calendar' }"
                  @click="setMonthlyRangeType('calendar')"
                >
                  Calendar Month
                </button>
              </div>
            </div>
            
            <!-- Month/Year Picker for Calendar Month -->
            <div v-if="monthlyRangeType === 'calendar'" class="month-year-picker">
              <div class="month-year-inputs">
                <div class="month-year-group">
                  <label>Month:</label>
                  <select v-model="selectedMonth" @change="onMonthYearChange" class="month-select">
                    <option value="01">January</option>
                    <option value="02">February</option>
                    <option value="03">March</option>
                    <option value="04">April</option>
                    <option value="05">May</option>
                    <option value="06">June</option>
                    <option value="07">July</option>
                    <option value="08">August</option>
                    <option value="09">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                </div>
                <div class="month-year-group">
                  <label>Year:</label>
                  <select v-model="selectedYear" @change="onMonthYearChange" class="year-select">
                    <option v-for="year in getAvailableYears()" :key="year" :value="year">{{ year }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Previous/Next Navigation for Weekly (7 days range and calendar week) and Monthly (30 days range) -->
          <div v-if="(selectedDateRange === 'weekly' && weeklyRangeType === '7days') || (selectedDateRange === 'weekly' && weeklyRangeType === 'calendar') || (selectedDateRange === 'monthly' && monthlyRangeType === '30days')" class="range-navigation">
            <button 
              @click="navigateRange('prev')" 
              class="nav-button prev-button"
              :title="selectedDateRange === 'weekly' ? 'Previous Week' : 'Previous Month'"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
              Previous {{ selectedDateRange === 'weekly' ? 'Week' : 'Month' }}
            </button>
            <span class="range-display">
              {{ getRangeDisplayText() }}
            </span>
            <button 
              @click="navigateRange('next')" 
              class="nav-button next-button"
              :disabled="!canNavigateNext()"
              :title="selectedDateRange === 'weekly' ? 'Next Week' : 'Next Month'"
            >
              Next {{ selectedDateRange === 'weekly' ? 'Week' : 'Month' }}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          </div>
          
          <!-- Custom Date Range Picker -->
          <div v-if="selectedDateRange === 'custom'" class="custom-date-range">
            <div class="date-range-inputs">
              <div class="date-input-group">
                <label>Start Date:</label>
                <input type="date" v-model="customStartDate" @change="updateCustomRange" />
              </div>
              <div class="date-input-group">
                <label>End Date:</label>
                <input type="date" v-model="customEndDate" @change="updateCustomRange" />
              </div>
            </div>
          </div>
          
          <!-- Selected Date Display (only for daily) -->
          <div v-if="selectedDateRange === 'daily'" class="selected-date-display">
            <DatePicker 
              v-model:dateValue="selectedDate" 
              :showQuickNav="selectedDateRange === 'daily'"
            />
          </div>
          
          <!-- Fetch Data Button (for weekly/monthly/custom) -->
          <div v-if="showFetchButton" class="fetch-data-button-container">
            <button 
              @click="triggerAllFetches" 
              class="fetch-data-button"
              :disabled="!canFetchData || isLoading"
            >
              <svg v-if="!isLoading" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              <span v-if="isLoading">Loading...</span>
              <span v-else-if="hasFetchedData">Reload Data</span>
              <span v-else>Fetch Data</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Advanced Loading Indicator -->
    <AdvancedLoadingIndicator
      v-if="isLoading"
      :title="loadingTitle"
      :message="loadingMessage"
      :progressText="loadingProgressText"
      :showProgress="true"
    />
    
    <!-- Main Content -->
    <div v-if="!isLoading && (selectedDateRange === 'daily' || hasFetchedData)">
      <!-- Performance Metrics -->
      <div class="performance-metrics-container">
        <div class="metrics-header">
          <h5 class="metrics-title">Performance Metrics</h5>
          <p class="metrics-subtitle">Energy consumption and cost analysis</p>
        </div>
        
        <div class="metrics-grid">
          <!-- Savings Card - Featured -->
          <div class="metric-card featured-card savings-card">
            <div class="metric-icon savings-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
            </div>
            <div class="metric-content">
              <div class="metric-label">Total Savings</div>
              <div class="metric-value savings-value">${{ formatNumber(performanceMetricsData['savings']) }}</div>
              <div class="metric-description">Saved by AI optimisation</div>
            </div>
          </div>

          <!-- Total Consumption -->
          <div class="metric-card consumption-card">
            <div class="metric-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
            </div>
            <div class="metric-content">
              <div class="metric-label">Consumption</div>
              <div class="metric-value">{{ formatNumber(performanceMetricsData['totalConsumption']) }} <span class="metric-unit">kWh</span></div>
              <div class="metric-description">Total energy consumed</div>
            </div>
          </div>

          <!-- Grid Import -->
          <div class="metric-card grid-import-card">
            <div class="metric-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="16 16 12 12 8 16"/>
                <line x1="12" y1="12" x2="12" y2="21"/>
                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                <polyline points="16 16 12 12 8 16"/>
              </svg>
            </div>
            <div class="metric-content">
              <div class="metric-label">Grid Import</div>
              <div class="metric-value">{{ formatNumber(performanceMetricsData['gridImport']) }} <span class="metric-unit">kWh</span></div>
              <div class="metric-description">Energy imported from grid</div>
            </div>
          </div>

          <!-- Net Cost -->
          <div class="metric-card net-cost-card">
            <div class="metric-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
            </div>
            <div class="metric-content">
              <div class="metric-label">Net Cost</div>
              <div class="metric-value">${{ formatNumber(performanceMetricsData['netCostAfterServiceFees']) }}</div>
              <div class="metric-description">After service fees</div>
            </div>
          </div>

          <!-- Usage Cost -->
          <div class="metric-card usage-card">
            <div class="metric-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
            </div>
            <div class="metric-content">
              <div class="metric-label">Usage Cost</div>
              <div class="metric-value">${{ formatNumber(performanceMetricsData['usageCost']) }}</div>
              <div class="metric-description">Grid import cost</div>
            </div>
          </div>

          <!-- Feed-in Cost -->
          <div class="metric-card feedin-card">
            <div class="metric-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </div>
            <div class="metric-content">
              <div class="metric-label">Feed-in Earnings</div>
              <div class="metric-value">${{ formatNumber(performanceMetricsData['feedInCost']) }}</div>
              <div class="metric-description">Grid export earnings</div>
            </div>
          </div>

          <!-- Original Cost -->
          <div class="metric-card original-cost-card">
            <div class="metric-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <div class="metric-content">
              <div class="metric-label">Original Cost</div>
              <div class="metric-value">${{ formatNumber(performanceMetricsData['originalCost']) }}</div>
              <div class="metric-description">Without AI optimisation</div>
            </div>
          </div>

          <!-- Original Cost per kWh -->
          <div class="metric-card original-cost-perkwh-card">
            <div class="metric-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </div>
            <div class="metric-content">
              <div class="metric-label">Org Cost per kWh</div>
              <div class="metric-value">${{ formatNumber(performanceMetricsData['originalCostPerkWh']) }} <span class="metric-unit">/kWh</span></div>
              <div class="metric-description">Cost per unit without AI</div>
            </div>
          </div>

          <!-- Optimised Cost -->
          <div class="metric-card optimised-cost-card">
            <div class="metric-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <div class="metric-content">
              <div class="metric-label">Optimised Cost</div>
              <div class="metric-value">${{ formatNumber(performanceMetricsData['costWithOptimisation']) }}</div>
              <div class="metric-description">With AI optimisation</div>
            </div>
          </div>

          <!-- Optimised Cost per kWh -->
          <div class="metric-card optimised-cost-perkwh-card">
            <div class="metric-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <div class="metric-content">
              <div class="metric-label">Opt Cost per kWh</div>
              <div class="metric-value">${{ parseFloat(performanceMetricsData['costWithOptimisationPerkWh']).toFixed(4) }}</div>
              <div class="metric-description">Cost per kWh with AI</div>
            </div>
          </div>
        </div>

        <!-- Formula Explanation Section -->
        <div class="formula-section">
          <button 
            @click="showFormulas = !showFormulas" 
            class="formula-toggle-btn"
            :class="{ expanded: showFormulas }"
          >
            <span class="toggle-icon">{{ showFormulas ? '▼' : '▶' }}</span>
            How is this calculated?
          </button>
          <transition name="formula-expand">
            <div v-if="showFormulas" class="formula-content">
              <!-- Optimized Cost -->
              <div class="formula-box">
                <div class="formula-title">Optimized Cost</div>
                <div class="formula-description">
                  This calculates the total energy cost when using AI optimization. It sums the cost of grid power at each time interval, where the cost depends on whether energy is being imported (buy price) or exported (sell price) based on the grid power direction.
                </div>
                <div class="formula-expression" v-html="renderLatex('C_{optimized} = \\sum_{i=0}^{287} c(t) \\cdot g(t) \\Delta t', true)"></div>
                <div class="formula-variables">
                  <p>Where:</p>
                  <ul>
                    <li><span v-html="renderLatex('c(t)')"></span> is the instantaneous cost at <span v-html="renderLatex('t')"></span>, defined as:</li>
                    <li class="formula-nested" v-html="renderLatex('c(t) = \\begin{cases} c_{buy}(t) \\iff g(t) > 0 \\\\ c_{sell}(t) \\iff g(t) \\leq 0 \\end{cases}', true)"></li>
                    <li><span v-html="renderLatex('g(t)')"></span> is the instantaneous grid power at <span v-html="renderLatex('t')"></span>, directly from Renox AI</li>
                  </ul>
                </div>
              </div>

              <!-- Usage Cost -->
              <div class="formula-box">
                <div class="formula-title">Usage Cost</div>
                <div class="formula-description">
                  This represents the cost of energy imported from the grid. It uses the same summation structure as optimized cost, but only charges for grid imports (when <span v-html="renderLatex('g(t) > 0')"></span>) and ignores any earnings from exports.
                </div>
                <div class="formula-expression" v-html="renderLatex('c(t) = \\begin{cases} c_{buy}(t) \\iff g(t) > 0 \\\\ 0 \\iff g(t) \\leq 0 \\end{cases}', true)"></div>
              </div>

              <!-- Feed-in Cost -->
              <div class="formula-box">
                <div class="formula-title">Feed-in Cost</div>
                <div class="formula-description">
                  This represents the earnings from exporting energy back to the grid. It uses the same summation structure as optimized cost, but only credits for grid exports (when <span v-html="renderLatex('g(t) \\leq 0')"></span>) and ignores any costs from imports.
                </div>
                <div class="formula-expression" v-html="renderLatex('c(t) = \\begin{cases} 0 \\iff g(t) > 0 \\\\ c_{sell}(t) \\iff g(t) \\leq 0 \\end{cases}', true)"></div>
              </div>

              <!-- Original Cost -->
              <div class="formula-box">
                <div class="formula-title">Original Cost</div>
                <div class="formula-description">
                  This calculates the total energy cost without AI optimization. It includes both grid power and battery power in the calculation, representing the baseline cost scenario where the battery operates independently without AI guidance.
                </div>
                <div class="formula-expression" v-html="renderLatex('C_{original} = \\sum_{i=0}^{287} c(t) \\cdot (g(t) + b(t)) \\Delta t', true)"></div>
                <div class="formula-variables">
                  <p>Where:</p>
                  <ul>
                    <li><span v-html="renderLatex('c(t)')"></span> is the instantaneous cost at <span v-html="renderLatex('t')"></span>, defined as:</li>
                    <li class="formula-nested" v-html="renderLatex('c(t) = \\begin{cases} c_{buy}(t) \\iff g(t) > 0 \\\\ c_{sell}(t) \\iff g(t) \\leq 0 \\end{cases}', true)"></li>
                    <li><span v-html="renderLatex('g(t)')"></span> is the instantaneous grid power at <span v-html="renderLatex('t')"></span> from Renox AI</li>
                    <li><span v-html="renderLatex('b(t)')"></span> is the instantaneous battery power at <span v-html="renderLatex('t')"></span> from Renox AI</li>
                  </ul>
                </div>
              </div>

              <!-- Total Consumption -->
              <div class="formula-box">
                <div class="formula-title">Total Consumption</div>
                <div class="formula-description">
                  This calculates the total energy consumption in kilowatt-hours. It uses the same structure as original cost but replaces the cost function with a unit value of 1, effectively summing all energy consumed (grid + battery) without applying pricing.
                </div>
                <div class="formula-expression" v-html="renderLatex('c(t) = 1 \\quad \\forall t \\in T', true)"></div>
              </div>

              <!-- Time Series Definition -->
              <div class="formula-box">
                <div class="formula-title">Time Series Definition</div>
                <div class="formula-description">
                  Consider a timeseries <span v-html="renderLatex('T')"></span> with fixed intervals <span v-html="renderLatex('t \\in \\{0, ..., 287\\}')"></span> and interval duration <span v-html="renderLatex('\\Delta t = 5/60')"></span> hours.
                </div>
              </div>
            </div>
          </transition>
        </div>

        <div class="metrics-footer">
          <button @click="navigateToReportPage" class="report-button">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
            View Full Performance Report
          </button>
        </div>
      </div>
      <br>
      
      <!-- Power Chart -->
      <div class="chart-container">
        <h5>{{ userPreferences.showSeparateSocChart ? 'Power Chart' : 'Power & SoC Chart' }}</h5>
        <div class="extra-details-box">
          <div class="extract-data-container">
            <button class="custom-button" @click="extractChartData('power')">
              Extract Data
            </button>
          </div>
          <div class="toggle-group">
            <label class="toggle-group-label">Data Group</label>
            <div class="toggle-buttons">
              <button
                v-for="option in chartToggleGroupingOptions"
                :key="option"
                class="toggle-btn"
                :class="{ active: chartToggleGroupingSelectedOption === option }"
                @click="selectToggleGroupingOption(option)"
              >
                {{ option }}
              </button>
            </div>
          </div>
          <div class="toggle-group">
            <label class="toggle-group-label">Data Type</label>
            <div class="toggle-buttons">
              <button
                v-for="option in chartToggleDataTypeOptions"
                :key="option"
                class="toggle-btn"
                :class="{ active: chartToggleDataTypeSelectedOption === option }"
                @click="selectToggleDataTypeOption(option)"
              >
                {{ option }}
              </button>
            </div>
          </div>
          <div class="toggle-group">
            <label class="toggle-group-label">Scale</label>
            <div class="toggle-buttons">
              <button
                class="toggle-btn"
                :class="{ active: !powerChartLogScale }"
                @click="setPowerChartLogScale(false)"
              >
                Linear
              </button>
              <button
                class="toggle-btn"
                :class="{ active: powerChartLogScale }"
                @click="setPowerChartLogScale(true)"
              >
                Log Scale
              </button>
            </div>
          </div>
        </div>
        
        <p v-if="chartDataPowerPrediction.length === 0 && chartDataPowerActual.length === 0" class="display-text-container">
          {{ displayTextPower }}
        </p>
        <PlotlyChart
          v-else
          :chartId="'power-chart'"
          chartTitle=""
          dataUnit="kW"
          :datasets="getPowerDatasets()"
          :secondaryDatasets="!userPreferences.showSeparateSocChart ? getSocDatasets() : []"
          :secondaryDataUnit="!userPreferences.showSeparateSocChart ? '%' : ''"
          :hiddenLegends="powerChartHiddenLegends"
          :rotateXAxisLabels="true"
          :xAxisTickAngle="-90"
          :yAxisType="powerChartLogScale ? 'log' : 'linear'"
          secondaryYAxisType="linear"
          @legends-updated="handlePowerChartHiddenLegendsUpdated"
        />
      </div>
      
      <!-- Prediction Accuracy Widget -->
      <PredictionAccuracyWidget
        v-if="isEvalEnabled"
        :actualLoad="actualData.load"
        :predictedLoad="predictionData.load"
        :actualPv="actualData.pv"
        :predictedPv="predictedPvForAccuracy"
      />

      <!-- BMS Simulator and Action Reasons -->
      <div v-if="accessLevelNumber == 10">
        <div v-if="isBmsSimulatorEnabled" class="bms-simulator-wrapper">
          <BmsSimulator :plantId="plantId" :dateStr="selectedDate" />
        </div>
        <ActionReasons v-if="isActionReasonsEnabled" :plantId="plantId" :date="selectedDate" />
      </div>
      
      <!-- Price Chart -->
      <div class="custom-divider"></div>
      <div class="chart-container">
        <h5>{{ userPreferences.showSeparateSocChart ? 'Price Chart' : 'Price & SoC Chart' }}</h5>
        <div class="extra-details-box">
          <div class="extract-data-container">
            <button class="custom-button" @click="extractChartData('price')">
              Extract Data
            </button>
          </div>
          <div v-if="accessLevelNumber == 10">
            <TariffAnalysisLink 
              :plantId="plantId"
              :alias="alias"
              :date="selectedDate"
              :retailerId="retailerId"
            />
          </div>
          <div class="toggle-group">
            <label class="toggle-group-label">Data Type</label>
            <div class="toggle-buttons">
              <button
                v-for="option in chartToggleDataTypeOptions"
                :key="option"
                class="toggle-btn"
                :class="{ active: chartToggleDataTypeSelectedOption === option }"
                @click="selectToggleDataTypeOption(option)"
              >
                {{ option }}
              </button>
            </div>
          </div>
          <div class="toggle-group">
            <label class="toggle-group-label">Scale</label>
            <div class="toggle-buttons">
              <button
                class="toggle-btn"
                :class="{ active: !priceChartLogScale }"
                @click="setPriceChartLogScale(false)"
              >
                Linear
              </button>
              <button
                class="toggle-btn"
                :class="{ active: priceChartLogScale }"
                @click="setPriceChartLogScale(true)"
              >
                Log Scale
              </button>
            </div>
          </div>
        </div>
        
        <p v-if="chartDataPricePrediction.length === 0 && chartDataPriceActual.length === 0" class="display-text-container">
          {{ displayTextPrice }}
        </p>
        <PlotlyChart
          v-else
          :chartId="'price-chart'"
          chartTitle=""
          dataUnit="AUD"
          :datasets="getPriceDatasets()"
          :secondaryDatasets="!userPreferences.showSeparateSocChart ? getSocDatasets() : []"
          :secondaryDataUnit="!userPreferences.showSeparateSocChart ? '%' : ''"
          :hiddenLegends="priceChartHiddenLegends"
          :rotateXAxisLabels="true"
          :xAxisTickAngle="-90"
          :yAxisType="priceChartLogScale ? 'log' : 'linear'"
          secondaryYAxisType="linear"
          @legends-updated="handlePriceChartHiddenLegendsUpdated"
        />
      </div>
      
      <!-- Price Accuracy Widget -->
      <PriceAccuracyWidget
        v-if="isEvalEnabled"
        :actualBuyPrice="actualData.buy_price"
        :predictedBuyPrice="predictedBuyPriceForAccuracy"
        :actualSellPrice="actualData.sell_price"
        :predictedSellPrice="predictedSellPriceForAccuracy"
      />
      
      <!-- SoC Chart -->
      <div v-if="userPreferences.showSeparateSocChart" class="custom-divider"></div>
      <div v-if="userPreferences.showSeparateSocChart" class="chart-container">
        <h5>SoC Chart</h5>
        <div class="extra-details-box">
          <div class="extract-data-container">
            <button class="custom-button" @click="extractChartData('soc')">
              Extract Data
            </button>
          </div>
          <div class="toggle-group">
            <label class="toggle-group-label">Scale</label>
            <div class="toggle-buttons">
              <button
                class="toggle-btn"
                :class="{ active: !socChartLogScale }"
                @click="setSocChartLogScale(false)"
              >
                Linear
              </button>
              <button
                class="toggle-btn"
                :class="{ active: socChartLogScale }"
                @click="setSocChartLogScale(true)"
              >
                Log Scale
              </button>
            </div>
          </div>
        </div>
        
        <p v-if="chartDataSocActual.length === 0" class="display-text-container">
          {{ displayTextSoc }}
        </p>
        <PlotlyChart
          v-else
          :chartId="'soc-chart'"
          chartTitle=""
          dataUnit="%"
          :datasets="chartDataSocActual"
          :yAxisMin="0"
          :yAxisMax="100"
          :isFilled="true"
          :rotateXAxisLabels="true"
          :xAxisTickAngle="-90"
          :yAxisType="socChartLogScale ? 'log' : 'linear'"
        />
      </div>    

      
      <div class="bottom-pillar"></div>
    </div>
  </div>
</template>

<script>
import base64 from 'base-64';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { apiRequest } from '@/services/apiService';
import PlotlyChart from '@/components/cards/PlotlyChart.vue';
import DatePicker from '../cards/DatePicker.vue';
import AdvancedLoadingIndicator from '../cards/AdvancedLoadingIndicator.vue';
import StyledDataBox from '../cards/StyledDataBox.vue';
import LocalTime from '../cards/LocalTime.vue';
import ActionReasons from '../cards/ActionReasons.vue';
import BmsSimulator from '../cards/BmsSimulator.vue';
import PredictionAccuracyWidget from '../cards/PredictionAccuracyWidget.vue';
import PriceAccuracyWidget from '../cards/PriceAccuracyWidget.vue';
import TariffAnalysisLink from '../cards/TariffAnalysisLink.vue';
import { 
  getToggleDataTypeState, 
  setToggleDataTypeState, 
  getToggleGroupingState, 
  setToggleGroupingState,
  getPowerChartLegendsState,
  getPriceChartLegendsState,
  setPowerChartLegendsState,
  setPriceChartLegendsState,
  getUserPreferences,
  setUserPreferences,
  getDefaultUserPreferences,
  getDevModeState,
  getPredictionEvaluationState,
  setPredictionEvaluationState,
  getPowerChartLogScaleState,
  setPowerChartLogScaleState,
  getPriceChartLogScaleState,
  setPriceChartLogScaleState,
  getSocChartLogScaleState,
  setSocChartLogScaleState,
  setDetailPageVersion
} from '@/services/cookieService';
import { eventBus } from '@/services/eventBus';

export default {
  name: 'BirdsEyeDetailView',
  components: {
    PlotlyChart,
    DatePicker,
    AdvancedLoadingIndicator,
    StyledDataBox,
    LocalTime,
    ActionReasons,
    BmsSimulator,
    PredictionAccuracyWidget,
    PriceAccuracyWidget,
    TariffAnalysisLink,
  },
  props: {
    plantId: {
      type: Number,
      required: true
    },
    alias: {
      type: String,
      default: ''
    },
    timezoneDataMap: {
      type: Object,
      default: () => ({ timezone_offset: 0, timezone_name: '' })
    },
    accessLevel: {
      type: [Number, String],
      default: 0
    },
    retailerId: {
      type: Number,
      default: null
    },
    dateAdded: {
      type: String,
      default: ''
    },
    retailerName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      selectedDate: this.getDate(),
      selectedDateRange: this.getDateRangeFromUrl(),
      rangeStartDate: this.getDate(), // Reference date for weekly/monthly ranges
      customStartDate: '',
      customEndDate: '',
      dateRangeOptions: [
        { value: 'daily', label: 'DAILY' },
        { value: 'weekly', label: 'WEEKLY' },
        { value: 'monthly', label: 'MONTHLY' },
        { value: 'custom', label: 'CUSTOM' }
      ],
      isLoading: false,
      loadingTitle: 'Loading Data...',
      loadingMessage: 'Please wait while we fetch your data',
      loadingProgressText: 'Initializing...',
      opacityLevelPrediction: 0.7,
      opacityLevelActual: 0.7,
      chartToggleGroupingOptions: ["PV", "Load", "Battery", "Grid", "All"],
      chartToggleDataTypeOptions: ["Predicted", "Actual", "Both"],
      powerChartHiddenLegends: [],
      priceChartHiddenLegends: [],
      chartToggleDataTypeSelectedOption: "Both",
      chartToggleGroupingSelectedOption: "All",
      displayTextPower: 'No power data to show',
      displayTextPrice: 'No price data to show',
      displayTextSoc: 'No SoC data to show',
      chartDataPowerPrediction: [],
      chartDataPowerActual: [],
      chartDataPricePrediction: [],
      chartDataPriceActual: [],
      chartDataSocActual: [],
      chartLabels: this.prepareLabels(5, 288),
      subDataUrl: "/api/v1.1/data",
      performanceMetricsData: {
        totalConsumption: 0,
        gridImport: 0,
        usageCost: 0,
        feedInCost: 0,
        originalCost: 0,
        costWithOptimisation: 0,
        originalCostPerkWh: 0,
        costWithOptimisationPerkWh: 0,
        savings: 0,
        netCostAfterServiceFees: 0,
      },
      actualData: {
        pv: [],
        load: [],
        battery: [],
        buy_price: [],
        sell_price: []
      },
      predictionData: {
        pv: [],
        pv_adjusted: [],
        load: [],
        battery: [],
        buy_price: [],
        sell_price: [],
        buy_price_adjusted: [],
        sell_price_adjusted: []
      },
      isEvalEnabled: true, // Default to visible
      isActionReasonsEnabled: false,
      isBmsSimulatorEnabled: false,
      isUserPreferencesEnabled: false,
      isPlantDevicesDataBoxVisible: false,
      plantDevicesDataMap: [],
      deviceSoHComparisonMap: {},
      userPreferences: getDefaultUserPreferences(),
      showFormulas: false,
      powerChartLogScale: false,
      priceChartLogScale: false,
      socChartLogScale: false,
      // Temporary storage for date range data
      dateRangeData: {
        actual: [],
        price: [],
        predictions: []
      },
      // Monthly range type: '30days' or 'calendar'
      monthlyRangeType: '30days',
      // Weekly range type: '7days' or 'calendar'
      weeklyRangeType: '7days',
      // Selected month/year for calendar month mode (format: 'YYYY-MM')
      selectedMonth: null,
      selectedYear: null,
      // Selected week start date for calendar week mode (format: 'YYYY-MM-DD', Monday of the week)
      selectedWeekStartDate: null,
      // Track if data has been fetched for the current range selection
      hasFetchedData: false,
      dataAvailabilityLoading: false,
      dataAvailabilityError: '',
      dataAvailability: {
        pv: { startDate: 'N/A', endDate: 'N/A' },
        load: { startDate: 'N/A', endDate: 'N/A' },
        price: { startDate: 'N/A', endDate: 'N/A' },
        renox: { startDate: 'Pending API endpoint', endDate: 'Pending API endpoint' }
      }
    };
  },
  computed: {
    accessLevelNumber() {
      return typeof this.accessLevel === 'string' ? parseInt(this.accessLevel, 10) : this.accessLevel;
    },
    predictedPvForAccuracy() {
      return this.mergeAdjustedPrediction(this.predictionData.pv_adjusted, this.predictionData.pv);
    },
    predictedBuyPriceForAccuracy() {
      return this.mergeAdjustedPrediction(this.predictionData.buy_price_adjusted, this.predictionData.buy_price);
    },
    predictedSellPriceForAccuracy() {
      return this.mergeAdjustedPrediction(this.predictionData.sell_price_adjusted, this.predictionData.sell_price);
    },
    formattedDateAdded() {
      if (!this.dateAdded) return 'N/A';
      try {
        const date = new Date(this.dateAdded);
        if (Number.isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString();
      } catch {
        return 'N/A';
      }
    },
    isLegacyDesign() {
      return false;
    },
    dateRangeArray() {
      if (this.selectedDateRange === 'daily') {
        return [this.selectedDate];
      } else if (this.selectedDateRange === 'weekly') {
        if (this.weeklyRangeType === 'calendar') {
          // Calendar week: Monday to Sunday of selected week
          if (this.selectedWeekStartDate) {
            return this.generateCalendarWeekRange(this.selectedWeekStartDate);
          }
          // Fallback to current week if not set
          const today = new Date();
          const currentWeekStart = this.getWeekStartDate(today);
          return this.generateCalendarWeekRange(currentWeekStart);
        } else {
          // Weekly: 7 days ending on rangeStartDate (going backwards)
          return this.generateDateRangeBackwards(this.rangeStartDate, 7);
        }
      } else if (this.selectedDateRange === 'monthly') {
        if (this.monthlyRangeType === 'calendar') {
          // Calendar month: first day to last day of selected month
          if (this.selectedYear && this.selectedMonth) {
            return this.generateCalendarMonthRange(parseInt(this.selectedYear), parseInt(this.selectedMonth));
          }
          // Fallback to current month if not set
          const today = new Date();
          return this.generateCalendarMonthRange(today.getFullYear(), today.getMonth() + 1);
        } else {
          // Monthly: 30 days ending on rangeStartDate (going backwards)
          return this.generateDateRangeBackwards(this.rangeStartDate, 30);
        }
      } else if (this.selectedDateRange === 'custom') {
        if (this.customStartDate && this.customEndDate) {
          return this.generateDateRange(this.customStartDate, null, this.customEndDate);
        }
        return [this.selectedDate];
      }
      return [this.selectedDate];
    },
    canFetchData() {
      if (this.selectedDateRange === 'daily') {
        return false; // No button needed for daily
      }
      if (this.selectedDateRange === 'weekly') {
        if (this.weeklyRangeType === 'calendar') {
          return !!this.selectedWeekStartDate;
        } else {
          return !!this.rangeStartDate;
        }
      }
      if (this.selectedDateRange === 'monthly') {
        if (this.monthlyRangeType === 'calendar') {
          return !!(this.selectedYear && this.selectedMonth);
        } else {
          return !!this.rangeStartDate;
        }
      }
      if (this.selectedDateRange === 'custom') {
        return !!(this.customStartDate && this.customEndDate && this.customStartDate <= this.customEndDate);
      }
      return false;
    },
    showFetchButton() {
      return this.selectedDateRange === 'weekly' || this.selectedDateRange === 'monthly' || this.selectedDateRange === 'custom';
    }
  },
  methods: {
    mergeAdjustedPrediction(adjusted, nonAdjusted) {
      const adj = adjusted || [];
      const raw = nonAdjusted || [];
      if (adj.length === 0) return raw;
      const len = Math.max(adj.length, raw.length);
      const result = [];
      for (let i = 0; i < len; i++) {
        const a = adj[i];
        const r = raw[i];
        const aValid = a != null && !isNaN(parseFloat(a));
        const rValid = r != null && !isNaN(parseFloat(r));
        result.push(aValid ? a : rValid ? r : null);
      }
      return result;
    },
    formatAvailabilityDate(dateValue) {
      if (!dateValue) return 'N/A';
      if (typeof dateValue === 'string') {
        const isoDate = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoDate) {
          return `${isoDate[3]}.${isoDate[2]}.${isoDate[1]}`;
        }
        const isoSlashDate = dateValue.match(/^(\d{4})\/(\d{2})\/(\d{2})/);
        if (isoSlashDate) {
          return `${isoSlashDate[3]}.${isoSlashDate[2]}.${isoSlashDate[1]}`;
        }
        const dottedDate = dateValue.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
        if (dottedDate) {
          return dateValue;
        }
        const slashDmy = dateValue.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
        if (slashDmy) {
          return `${slashDmy[1]}.${slashDmy[2]}.${slashDmy[3]}`;
        }
      }
      return 'N/A';
    },
    extractAvailabilityFromResponse(responseData) {
      if (!responseData) {
        return { startDate: 'N/A', endDate: 'N/A' };
      }
      if (Array.isArray(responseData) && responseData.length > 0) {
        const dateCandidates = responseData
          .map((item) => item?.date_created || item?.model_date || item?.date || item?.created_at)
          .filter((value) => !!value)
          .sort((a, b) => new Date(a) - new Date(b));

        if (dateCandidates.length > 0) {
          return {
            startDate: this.formatAvailabilityDate(dateCandidates[0]),
            endDate: this.formatAvailabilityDate(dateCandidates[dateCandidates.length - 1])
          };
        }
      }

      const target = Array.isArray(responseData) ? (responseData[0] || {}) : responseData;
      const startKeys = ['start_date', 'startDate', 'date_start', 'min_date', 'first_date', 'from_date'];
      const endKeys = ['end_date', 'endDate', 'date_end', 'max_date', 'last_date', 'to_date'];

      const rawStart = startKeys.map((key) => target[key]).find((value) => value != null && value !== '');
      const rawEnd = endKeys.map((key) => target[key]).find((value) => value != null && value !== '');

      return {
        startDate: this.formatAvailabilityDate(rawStart),
        endDate: this.formatAvailabilityDate(rawEnd)
      };
    },
    async fetchAvailabilityInfo(endpoint, payloadKey = 'plant_id') {
      const bodyData = {
        [payloadKey]: this.plantId
      };
      const url = `${this.subDataUrl}${endpoint}`;
      const response = await apiRequest('post', url, bodyData);
      if (response.errorCode !== 0 || response.data == null) {
        return { startDate: 'N/A', endDate: 'N/A' };
      }
      return this.extractAvailabilityFromResponse(response.data);
    },
    async fetchAvailabilityInfoWithFallback(endpoints, payloadKeys = ['plant_id']) {
      for (const endpoint of endpoints) {
        for (const payloadKey of payloadKeys) {
          try {
            const result = await this.fetchAvailabilityInfo(endpoint, payloadKey);
            if (result.startDate !== 'N/A' || result.endDate !== 'N/A') {
              return result;
            }
          } catch (error) {
            // Try next endpoint/payload variant
          }
        }
      }
      return { startDate: 'N/A', endDate: 'N/A' };
    },
    async fetchDataAvailability() {
      this.dataAvailabilityLoading = true;
      this.dataAvailabilityError = '';
      try {
        const [pvAvailability, loadAvailability, priceAvailability] = await Promise.all([
          this.fetchAvailabilityInfoWithFallback([
            '/pv_prediction/info/get',
            '/pv_predictions/info/get',
            '/pv_prediction/get_info'
          ]),
          this.fetchAvailabilityInfoWithFallback([
            '/load_prediction/info/get'
          ]),
          this.fetchAvailabilityInfoWithFallback([
            '/price_prediction/info/get'
          ])
        ]);
        const renoxAvailability = await this.fetchAvailabilityInfoWithFallback(
          ['/proxy/rx/plant/available/dates'],
          ['plantId']
        );

        this.dataAvailability.pv = pvAvailability;
        this.dataAvailability.load = loadAvailability;
        this.dataAvailability.price = priceAvailability;
        this.dataAvailability.renox = renoxAvailability;
      } catch (error) {
        this.dataAvailabilityError = 'Unable to load all availability data right now.';
      } finally {
        this.dataAvailabilityLoading = false;
      }
    },
    getDate() {
      const urlParams = new URLSearchParams(window.location.search);
      const dateFromUrl = urlParams.get('date');
      if (dateFromUrl) {
        return dateFromUrl;
      }
      const today = new Date();
      const year = today.getFullYear();
      const month = (today.getMonth() + 1).toString().padStart(2, '0');
      const day = today.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    getDateRangeFromUrl() {
      // Try to get from Vue Router first, fallback to URLSearchParams
      const rangeFromUrl = this.$route?.query?.range || new URLSearchParams(window.location.search).get('range');
      if (rangeFromUrl && ['daily', 'weekly', 'monthly', 'custom'].includes(rangeFromUrl)) {
        return rangeFromUrl;
      }
      return 'daily';
    },
    updateUrlRange(range) {
      // Update URL query parameter using Vue Router
      const query = { ...this.$route.query };
      if (range) {
        query.range = range;
      } else {
        delete query.range;
      }
      this.$router.push({ query }).catch(() => {
        // Ignore navigation duplicate errors
      });
    },
    generateDateRange(startDate, days, endDate = null) {
      const dates = [];
      const start = new Date(startDate + 'T00:00:00');
      let end;
      
      if (endDate) {
        end = new Date(endDate + 'T00:00:00');
      } else {
        end = new Date(start);
        end.setDate(end.getDate() + (days - 1));
      }
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const year = d.getFullYear();
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        dates.push(`${year}-${month}-${day}`);
      }
      
      return dates;
    },
    generateDateRangeBackwards(endDate, days) {
      // Generate date range going backwards from endDate
      const dates = [];
      const end = new Date(endDate + 'T00:00:00');
      const start = new Date(end);
      start.setDate(start.getDate() - (days - 1));
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const year = d.getFullYear();
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        dates.push(`${year}-${month}-${day}`);
      }
      
      return dates;
    },
    initializeRangeState(range) {
      if (range === 'custom') {
        if (!this.customStartDate || !this.customEndDate) {
          const today = new Date();
          const year = today.getFullYear();
          const month = (today.getMonth() + 1).toString().padStart(2, '0');
          const day = today.getDate().toString().padStart(2, '0');
          this.customStartDate = `${year}-${month}-${day}`;
          this.customEndDate = `${year}-${month}-${day}`;
        }
      } else if (range === 'weekly' || range === 'monthly') {
        // Set rangeStartDate to selectedDate (or today if not set)
        if (!this.rangeStartDate) {
          this.rangeStartDate = this.selectedDate || this.getDate();
        }
        
        // Initialize weekly range type and week start date if switching to weekly
        if (range === 'weekly') {
          if (this.weeklyRangeType === 'calendar' && !this.selectedWeekStartDate) {
            const today = new Date();
            const weekStart = this.getWeekStartDate(today);
            this.selectedWeekStartDate = weekStart;
          }
        }
        
        // Initialize monthly range type and month/year if switching to monthly
        if (range === 'monthly') {
          if (this.monthlyRangeType === 'calendar' && (!this.selectedYear || !this.selectedMonth)) {
            const today = new Date();
            this.selectedYear = today.getFullYear().toString();
            this.selectedMonth = (today.getMonth() + 1).toString().padStart(2, '0');
          }
        }
      }
    },
    selectDateRange(range) {
      this.selectedDateRange = range;
      this.updateUrlRange(range);
      this.initializeRangeState(range);
      // Reset fetch flag when switching to weekly/monthly/custom
      if (range !== 'daily') {
        this.hasFetchedData = false;
      }
      // Only auto-fetch for daily tab
      if (range === 'daily') {
        this.hasFetchedData = true; // Daily auto-fetches, so mark as fetched
        this.triggerAllFetches();
      }
    },
    navigateRange(direction) {
      if (this.selectedDateRange !== 'weekly' && this.selectedDateRange !== 'monthly') {
        return;
      }
      
      // Handle calendar week mode
      if (this.selectedDateRange === 'weekly' && this.weeklyRangeType === 'calendar') {
        if (!this.selectedWeekStartDate) {
          const today = new Date();
          const weekStart = this.getWeekStartDate(today);
          this.selectedWeekStartDate = weekStart;
        }
        
        const currentWeekStart = new Date(this.selectedWeekStartDate + 'T00:00:00');
        const daysToMove = direction === 'prev' ? -7 : 7;
        currentWeekStart.setDate(currentWeekStart.getDate() + daysToMove);
        
        // Don't allow going into the future beyond current week
        if (direction === 'next') {
          const today = new Date();
          const currentWeekStartToday = this.getWeekStartDate(today);
          if (currentWeekStart > currentWeekStartToday) {
            currentWeekStart.setTime(new Date(currentWeekStartToday + 'T00:00:00').getTime());
          }
        }
        
        const year = currentWeekStart.getFullYear();
        const month = (currentWeekStart.getMonth() + 1).toString().padStart(2, '0');
        const day = currentWeekStart.getDate().toString().padStart(2, '0');
        this.selectedWeekStartDate = `${year}-${month}-${day}`;
        // Reset fetch flag when navigating ranges
        this.hasFetchedData = false;
        // Don't auto-fetch, wait for button click
        return;
      }
      
      // Handle calendar month mode
      if (this.selectedDateRange === 'monthly' && this.monthlyRangeType === 'calendar') {
        if (!this.selectedYear || !this.selectedMonth) {
          const today = new Date();
          this.selectedYear = today.getFullYear().toString();
          this.selectedMonth = (today.getMonth() + 1).toString().padStart(2, '0');
        }
        
        let year = parseInt(this.selectedYear);
        let month = parseInt(this.selectedMonth);
        
        if (direction === 'prev') {
          month -= 1;
          if (month < 1) {
            month = 12;
            year -= 1;
          }
        } else if (direction === 'next') {
          month += 1;
          if (month > 12) {
            month = 1;
            year += 1;
          }
          // Don't allow going into the future beyond current month
          const today = new Date();
          const currentYear = today.getFullYear();
          const currentMonth = today.getMonth() + 1;
          if (year > currentYear || (year === currentYear && month > currentMonth)) {
            year = currentYear;
            month = currentMonth;
          }
        }
        
        this.selectedYear = year.toString();
        this.selectedMonth = month.toString().padStart(2, '0');
        // Don't auto-fetch, wait for button click
        return;
      }
      
      // Handle weekly and 30-day monthly range
      const currentDate = new Date(this.rangeStartDate + 'T00:00:00');
      const daysToMove = this.selectedDateRange === 'weekly' ? 7 : 30;
      
      if (direction === 'prev') {
        currentDate.setDate(currentDate.getDate() - daysToMove);
      } else if (direction === 'next') {
        currentDate.setDate(currentDate.getDate() + daysToMove);
        // Don't allow going into the future beyond today
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (currentDate > today) {
          currentDate.setTime(today.getTime());
        }
      }
      
      const year = currentDate.getFullYear();
      const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
      const day = currentDate.getDate().toString().padStart(2, '0');
      this.rangeStartDate = `${year}-${month}-${day}`;
      
      // Reset fetch flag when navigating ranges
      this.hasFetchedData = false;
      // Don't auto-fetch, wait for button click
    },
    canNavigateNext() {
      if (this.selectedDateRange !== 'weekly' && this.selectedDateRange !== 'monthly') {
        return false;
      }
      
      // Handle calendar week mode
      if (this.selectedDateRange === 'weekly' && this.weeklyRangeType === 'calendar') {
        if (!this.selectedWeekStartDate) {
          return false;
        }
        const today = new Date();
        const currentWeekStart = this.getWeekStartDate(today);
        const selectedWeekStart = new Date(this.selectedWeekStartDate + 'T00:00:00');
        return selectedWeekStart < currentWeekStart;
      }
      
      // Handle calendar month mode
      if (this.selectedDateRange === 'monthly' && this.monthlyRangeType === 'calendar') {
        if (!this.selectedYear || !this.selectedMonth) {
          return false;
        }
        const currentYear = parseInt(this.selectedYear);
        const currentMonth = parseInt(this.selectedMonth);
        const today = new Date();
        const todayYear = today.getFullYear();
        const todayMonth = today.getMonth() + 1;
        
        // Can navigate next if current month is before today's month
        return currentYear < todayYear || (currentYear === todayYear && currentMonth < todayMonth);
      }
      
      // Handle weekly and 30-day monthly range
      const rangeEnd = new Date(this.rangeStartDate + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return rangeEnd < today;
    },
    getRangeDisplayText() {
      if (this.selectedDateRange === 'weekly') {
        // Calendar week mode: show week range
        if (this.weeklyRangeType === 'calendar' && this.selectedWeekStartDate) {
          const dates = this.dateRangeArray;
          if (dates.length > 0) {
            const start = new Date(dates[0] + 'T00:00:00');
            const end = new Date(dates[dates.length - 1] + 'T00:00:00');
            return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
          }
        }
        // 7 days range mode: show date range
        const dates = this.dateRangeArray;
        if (dates.length > 0) {
          const start = new Date(dates[0] + 'T00:00:00');
          const end = new Date(dates[dates.length - 1] + 'T00:00:00');
          return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        }
      } else if (this.selectedDateRange === 'monthly') {
        // Calendar month mode: show month name and year
        if (this.monthlyRangeType === 'calendar' && this.selectedYear && this.selectedMonth) {
          const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                             'July', 'August', 'September', 'October', 'November', 'December'];
          const monthIndex = parseInt(this.selectedMonth) - 1;
          return `${monthNames[monthIndex]} ${this.selectedYear}`;
        }
        // 30-day range mode: show date range
        const dates = this.dateRangeArray;
        if (dates.length > 0) {
          const start = new Date(dates[0] + 'T00:00:00');
          const end = new Date(dates[dates.length - 1] + 'T00:00:00');
          return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        }
      }
      return '';
    },
    updateCustomRange() {
      // Reset fetch flag when custom dates change
      this.hasFetchedData = false;
      // Don't auto-fetch, wait for button click
    },
    generateCalendarMonthRange(year, month) {
      const startDate = this.getMonthStartDate(year, month);
      const endDate = this.getMonthEndDate(year, month);
      return this.generateDateRange(startDate, null, endDate);
    },
    getMonthStartDate(year, month) {
      const date = new Date(year, month - 1, 1);
      const yearStr = date.getFullYear();
      const monthStr = (date.getMonth() + 1).toString().padStart(2, '0');
      const dayStr = '01';
      return `${yearStr}-${monthStr}-${dayStr}`;
    },
    getMonthEndDate(year, month) {
      // Get last day of month by going to first day of next month and subtracting 1 day
      const date = new Date(year, month, 0);
      const yearStr = date.getFullYear();
      const monthStr = (date.getMonth() + 1).toString().padStart(2, '0');
      const dayStr = date.getDate().toString().padStart(2, '0');
      return `${yearStr}-${monthStr}-${dayStr}`;
    },
    setMonthlyRangeType(type) {
      this.monthlyRangeType = type;
      if (type === 'calendar' && (!this.selectedYear || !this.selectedMonth)) {
        const today = new Date();
        this.selectedYear = today.getFullYear().toString();
        this.selectedMonth = (today.getMonth() + 1).toString().padStart(2, '0');
      }
      // Reset fetch flag when changing range type
      this.hasFetchedData = false;
      // Don't auto-fetch, wait for button click
    },
    setWeeklyRangeType(type) {
      this.weeklyRangeType = type;
      if (type === 'calendar' && !this.selectedWeekStartDate) {
        const today = new Date();
        const weekStart = this.getWeekStartDate(today);
        this.selectedWeekStartDate = weekStart;
      }
      // Reset fetch flag when changing range type
      this.hasFetchedData = false;
      // Don't auto-fetch, wait for button click
    },
    onMonthYearChange() {
      // Reset fetch flag when changing month/year
      this.hasFetchedData = false;
      // Don't auto-fetch, wait for button click
    },
    onWeekStartDateChange() {
      // Ensure the selected date is a Monday
      if (this.selectedWeekStartDate) {
        const date = new Date(this.selectedWeekStartDate + 'T00:00:00');
        const dayOfWeek = date.getDay();
        // Adjust to Monday (0 = Sunday, 1 = Monday, etc.)
        const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        date.setDate(date.getDate() + diff);
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        this.selectedWeekStartDate = `${year}-${month}-${day}`;
      }
      // Reset fetch flag when changing week start date
      this.hasFetchedData = false;
      // Don't auto-fetch, wait for button click
    },
    generateCalendarWeekRange(weekStartDate) {
      const startDate = weekStartDate;
      const endDate = this.getWeekEndDate(weekStartDate);
      return this.generateDateRange(startDate, null, endDate);
    },
    getWeekStartDate(date) {
      // Get Monday of the week for the given date
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      const dayOfWeek = d.getDay();
      // Adjust to Monday (0 = Sunday, 1 = Monday, etc.)
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      d.setDate(d.getDate() + diff);
      const year = d.getFullYear();
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const day = d.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    getWeekEndDate(weekStartDate) {
      // Get Sunday of the week (6 days after Monday)
      const startDate = new Date(weekStartDate + 'T00:00:00');
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 6);
      const year = endDate.getFullYear();
      const month = (endDate.getMonth() + 1).toString().padStart(2, '0');
      const day = endDate.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    getAvailableYears() {
      const currentYear = new Date().getFullYear();
      const years = [];
      // Show current year and previous 5 years
      for (let i = 0; i <= 5; i++) {
        years.push(currentYear - i);
      }
      return years;
    },
    prepareLabels(intervalInMins, length) {
      const labels = [];
      let minutes = 0;
      for (let i = 0; i < length; i++) {
        const hours = Math.floor(minutes / 60).toString().padStart(2, '0');
        const mins = (minutes % 60).toString().padStart(2, '0');
        labels.push(`${hours}:${mins}`);
        minutes += intervalInMins;
      }
      return labels;
    },
    async triggerAllFetches() {
      this.isLoading = true;
      this.loadingTitle = 'Loading Data...';
      if (this.selectedDateRange === 'daily') {
        this.loadingMessage = 'Fetching data for selected date';
      } else {
        this.loadingMessage = 'Fetching data for selected date range';
      }
      this.loadingProgressText = 'Initializing...';
      
      this.resetAllDataLists();
      this.readFromCookies();
      
      try {
        // For single date, use existing logic
        if (this.dateRangeArray.length === 1) {
          this.loadingProgressText = 'Fetching data...';
          await this.fetchDataForDate(this.dateRangeArray[0], true);
          if (this.userPreferences.priceActualBuySellRX) {
            await this.fetchLocalVoltPriceDataFromRedxProxy();
          }
          this.loadingProgressText = 'Finalizing...';
          await this.getPerformanceMetricsData();
          await this.getTimezoneData();
        } else {
          // For multiple dates, use bulk APIs only (no individual per-day APIs except get_summary)
          const startDate = this.dateRangeArray[0];
          const endDate = this.dateRangeArray[this.dateRangeArray.length - 1];
          
          this.loadingProgressText = 'Fetching bulk actual data...';
          const bulkActualData = await this.fetchBulkActualData(startDate, endDate);
          
          this.loadingProgressText = 'Fetching bulk prediction data...';
          const bulkPredictionData = await this.fetchBulkPredictionData(startDate, endDate);
          
          // Convert bulk API responses to format expected by aggregateMultiDayData
          // Note: Price actual and battery actions are not included in bulk APIs, so they will be empty
          const allDateData = this.convertBulkDataToDateArray(
            this.dateRangeArray,
            bulkActualData,
            bulkPredictionData,
            [] // No individual API results for price/battery
          );
          
          this.loadingProgressText = 'Processing data...';
          this.aggregateMultiDayData(allDateData);
          
          // get_summary API (getNetCostAfterServiceFees) is called in getPerformanceMetricsData()
          // for bulk ranges, so we don't need to call it separately here
          
          this.loadingProgressText = 'Finalizing...';
          await this.getPerformanceMetricsData();
          await this.getTimezoneData();
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setTimeout(() => {
          this.isLoading = false;
          // Mark data as fetched after loading completes
          this.hasFetchedData = true;
        }, 300);
      }
    },
    async fetchDataForDate(date, updateDirectly = true) {
      // Reset prediction arrays if updating directly
      if (updateDirectly) {
        this.chartDataPowerPrediction = [];
        this.chartDataPricePrediction = [];
      }
      
      // Fetch all data for a single date
      const [actualData, priceData, pvData, pricePredData, loadData, batteryData] = await Promise.all([
        this.fetchActualDataFromRedx(date, updateDirectly),
        this.fetchPriceActualData(date, updateDirectly),
        this.fetchPvPredictions(date, updateDirectly),
        this.fetchPricePredictions(date, updateDirectly),
        this.fetchLoadPredictions(date, updateDirectly),
        this.fetchBatteryActionPredictions(date, updateDirectly),
      ]);
      
      // Calculate grid power if we have the required data (use setTimeout to ensure all data is set)
      if (updateDirectly) {
        setTimeout(() => {
          if (this.predictionData['load'] && this.predictionData['pv_adjusted'] && this.predictionData['battery']) {
            this.calculateGridPowerPredictions();
          }
        }, 100);
      }
      
      return {
        actual: actualData,
        price: priceData,
        pv: pvData,
        pricePred: pricePredData,
        load: loadData,
        battery: batteryData
      };
    },
    calculateGridPowerPredictions() {
      try {
        if (this.predictionData['load'] && 
            this.predictionData['pv_adjusted'] && 
            this.predictionData['battery']) {
          
          const gridPowerData = [];
          for (let index = 0; index < this.predictionData['load'].length; index++) {
            const gridPrediction = parseFloat(
              (this.predictionData['pv_adjusted'][index] - 
              this.predictionData['load'][index] + 
              this.predictionData['battery'][index]
              ).toFixed(2)
            ) * -1;
            gridPowerData.push(gridPrediction);
          }

          // Remove existing grid prediction if any
          this.chartDataPowerPrediction = this.chartDataPowerPrediction.filter(
            ds => ds.group !== 'grid_prediction'
          );

          this.chartDataPowerPrediction.push({
            group: 'grid_prediction',
            name: 'Predicted Grid Power',
            values: gridPowerData,
            color: `rgba(54, 162, 235, ${this.opacityLevelPrediction})`,
            labels: this.chartLabels,
          });
        }
      } catch (error) {
        console.error('Error calculating grid prediction:', error);
      }
    },
    convertBulkDataToDateArray(dateRangeArray, bulkActualData, bulkPredictionData, priceAndBatteryResults) {
      // Convert bulk API responses to the format expected by aggregateMultiDayData
      // Note: priceAndBatteryResults is kept for backward compatibility but should be empty for bulk mode
      const allDateData = [];
      const missingDates = [];
      
      dateRangeArray.forEach(date => {
        const dateData = {
          actual: null,
          pv: [],
          load: [],
          battery: [], // Battery actions not in bulk APIs
          price: null, // Will be set if priceActual is available
          pricePred: []
        };
        
        let hasData = false;
        
        // Get actual data from bulk response
        if (bulkActualData[date]) {
          dateData.actual = bulkActualData[date];
          hasData = true;
        }
        
        // Get prediction data from bulk response
        if (bulkPredictionData[date]) {
          const predictions = bulkPredictionData[date];
          if (predictions.pv && predictions.pv.length > 0) {
            dateData.pv = predictions.pv;
            hasData = true;
          }
          if (predictions.load && predictions.load.length > 0) {
            dateData.load = predictions.load;
            hasData = true;
          }
          if (predictions.price && predictions.price.length > 0) {
            dateData.pricePred = predictions.price;
            hasData = true;
          }
          // Get price actual data from bulk prediction response
          if (predictions.priceActual && predictions.priceActual.length > 0) {
            dateData.price = { actual: predictions.priceActual };
            hasData = true;
          }
        }
        
        // Battery actions are not available in bulk APIs
        // They will remain empty for bulk date ranges
        
        // Track dates with missing data for logging
        if (!hasData) {
          missingDates.push(date);
        }
        
        allDateData.push({ date, data: dateData });
      });
      
      // Log warning if some dates are missing data
      if (missingDates.length > 0) {
        console.warn(`Missing data for ${missingDates.length} date(s):`, missingDates);
      }
      
      return allDateData;
    },
    aggregateMultiDayData(allDateData) {
      // Initialize dataset maps
      const powerActualDatasets = {};
      const powerPredictionDatasets = {};
      const priceActualDatasets = {};
      const pricePredictionDatasets = {};
      const socData = [];
      const combinedLabels = [];
      
      // Process each date's data
      allDateData.forEach(({ date, data }) => {
        const dayLabels = this.prepareLabels(5, 288);
        
        dayLabels.forEach((timeLabel, timeIndex) => {
          const dateTimeLabel = `${date} ${timeLabel}`;
          combinedLabels.push(dateTimeLabel);
          
          // Aggregate power actual data
          if (data.actual && data.actual.power) {
            data.actual.power.forEach(dataset => {
              if (!powerActualDatasets[dataset.name]) {
                powerActualDatasets[dataset.name] = {
                  group: dataset.group,
                  name: dataset.name,
                  values: [],
                  color: dataset.color,
                  labels: []
                };
              }
              powerActualDatasets[dataset.name].values.push(
                dataset.values[timeIndex] !== undefined ? dataset.values[timeIndex] : null
              );
            });
          }
          
          // Aggregate power prediction data
          if (data.pv && data.pv.length > 0) {
            data.pv.forEach(dataset => {
              if (!powerPredictionDatasets[dataset.name]) {
                powerPredictionDatasets[dataset.name] = {
                  group: dataset.group,
                  name: dataset.name,
                  values: [],
                  color: dataset.color,
                  labels: []
                };
              }
              powerPredictionDatasets[dataset.name].values.push(
                dataset.values[timeIndex] !== undefined ? dataset.values[timeIndex] : null
              );
            });
          }
          
          // Similar for load, battery, etc.
          if (data.load && data.load.length > 0) {
            data.load.forEach(dataset => {
              if (!powerPredictionDatasets[dataset.name]) {
                powerPredictionDatasets[dataset.name] = {
                  group: dataset.group,
                  name: dataset.name,
                  values: [],
                  color: dataset.color,
                  labels: []
                };
              }
              powerPredictionDatasets[dataset.name].values.push(
                dataset.values[timeIndex] !== undefined ? dataset.values[timeIndex] : null
              );
            });
          }
          
          if (data.battery && data.battery.length > 0) {
            data.battery.forEach(dataset => {
              if (!powerPredictionDatasets[dataset.name]) {
                powerPredictionDatasets[dataset.name] = {
                  group: dataset.group,
                  name: dataset.name,
                  values: [],
                  color: dataset.color,
                  labels: []
                };
              }
              powerPredictionDatasets[dataset.name].values.push(
                dataset.values[timeIndex] !== undefined ? dataset.values[timeIndex] : null
              );
            });
          }
          
          // Aggregate price data
          if (data.price && data.price.actual) {
            data.price.actual.forEach(dataset => {
              if (!priceActualDatasets[dataset.name]) {
                priceActualDatasets[dataset.name] = {
                  group: dataset.group,
                  name: dataset.name,
                  values: [],
                  color: dataset.color,
                  labels: []
                };
              }
              priceActualDatasets[dataset.name].values.push(
                dataset.values[timeIndex] !== undefined ? dataset.values[timeIndex] : null
              );
            });
          }
          
          if (data.pricePred && data.pricePred.length > 0) {
            data.pricePred.forEach(dataset => {
              if (!pricePredictionDatasets[dataset.name]) {
                pricePredictionDatasets[dataset.name] = {
                  group: dataset.group,
                  name: dataset.name,
                  values: [],
                  color: dataset.color,
                  labels: []
                };
              }
              pricePredictionDatasets[dataset.name].values.push(
                dataset.values[timeIndex] !== undefined ? dataset.values[timeIndex] : null
              );
            });
          }
          
          // Aggregate SoC
          if (data.actual && data.actual.soc && data.actual.soc.length > 0) {
            socData.push(
              data.actual.soc[0].values[timeIndex] !== undefined 
                ? data.actual.soc[0].values[timeIndex] 
                : null
            );
          } else {
            socData.push(null);
          }
        });
      });
      
      // Update chart data arrays
      this.chartDataPowerActual = Object.values(powerActualDatasets).map(ds => ({
        ...ds,
        labels: combinedLabels
      }));
      
      this.chartDataPowerPrediction = Object.values(powerPredictionDatasets).map(ds => ({
        ...ds,
        labels: combinedLabels
      }));
      
      this.chartDataPriceActual = Object.values(priceActualDatasets).map(ds => ({
        ...ds,
        labels: combinedLabels
      }));
      
      this.chartDataPricePrediction = Object.values(pricePredictionDatasets).map(ds => ({
        ...ds,
        labels: combinedLabels
      }));
      
      if (socData.length > 0) {
        this.chartDataSocActual = [{
          group: 'soc_actual',
          name: 'Actual SoC',
          values: socData,
          color: `rgba(64, 224, 208, ${this.opacityLevelActual})`,
          labels: combinedLabels
        }];
      }
      
      this.chartLabels = combinedLabels;
      
      // Update actualData and predictionData for accuracy widgets
      // Extract arrays from aggregated datasets
      this.actualData = {
        pv: [],
        load: [],
        battery: [],
        buy_price: [],
        sell_price: []
      };
      
      this.predictionData = {
        pv: [],
        pv_adjusted: [],
        load: [],
        battery: [],
        buy_price: [],
        sell_price: [],
        buy_price_adjusted: [],
        sell_price_adjusted: []
      };
      
      // Extract PV actual data
      const pvActualDataset = powerActualDatasets['Actual PV Power'];
      if (pvActualDataset) {
        this.actualData.pv = pvActualDataset.values.map(v => v != null ? v : null);
      }
      
      // Extract Load actual data
      const loadActualDataset = powerActualDatasets['Actual Load Power'];
      if (loadActualDataset) {
        this.actualData.load = loadActualDataset.values.map(v => v != null ? v : null);
      }
      
      // Extract PV adjusted prediction data
      const pvAdjustedDataset = powerPredictionDatasets['Predicted PV Power (adjusted)'];
      if (pvAdjustedDataset) {
        this.predictionData.pv_adjusted = pvAdjustedDataset.values.map(v => v != null ? v : null);
      }

      const pvPredDataset = powerPredictionDatasets['Predicted PV Power'];
      if (pvPredDataset) {
        this.predictionData.pv = pvPredDataset.values.map(v => v != null ? v : null);
      }
      
      // Extract Load prediction data
      const loadPredDataset = powerPredictionDatasets['Predicted Load'];
      if (loadPredDataset) {
        this.predictionData.load = loadPredDataset.values.map(v => v != null ? v : null);
      }
      
      // Extract Buy Price actual data
      const buyPriceActualDataset = priceActualDatasets['Actual Price Buy'];
      if (buyPriceActualDataset) {
        this.actualData.buy_price = buyPriceActualDataset.values.map(v => v != null ? v : null);
      }
      
      // Extract Sell Price actual data
      const sellPriceActualDataset = priceActualDatasets['Actual Price Sell'];
      if (sellPriceActualDataset) {
        this.actualData.sell_price = sellPriceActualDataset.values.map(v => v != null ? v : null);
      }
      
      // Extract Buy Price prediction data
      const buyPricePredDataset = pricePredictionDatasets['Predicted Price Buy'];
      if (buyPricePredDataset) {
        this.predictionData.buy_price = buyPricePredDataset.values.map(v => v != null ? v : null);
      }
      
      // Extract Sell Price prediction data
      const sellPricePredDataset = pricePredictionDatasets['Predicted Price Sell'];
      if (sellPricePredDataset) {
        this.predictionData.sell_price = sellPricePredDataset.values.map(v => v != null ? v : null);
      }

      const buyPriceAdjustedDataset = pricePredictionDatasets['Predicted Price Buy (adjusted)'];
      if (buyPriceAdjustedDataset) {
        this.predictionData.buy_price_adjusted = buyPriceAdjustedDataset.values.map(v => v != null ? v : null);
      }

      const sellPriceAdjustedDataset = pricePredictionDatasets['Predicted Price Sell (adjusted)'];
      if (sellPriceAdjustedDataset) {
        this.predictionData.sell_price_adjusted = sellPriceAdjustedDataset.values.map(v => v != null ? v : null);
      }
    },
    resetAllDataLists() {
      this.chartDataPowerPrediction = [];
      this.chartDataPowerActual = [];
      this.chartDataPricePrediction = [];
      this.chartDataPriceActual = [];
      this.chartDataSocActual = [];
      // Reset actualData and predictionData to ensure widgets recalculate
      this.actualData = {
        pv: [],
        load: [],
        battery: [],
        buy_price: [],
        sell_price: []
      };
      this.predictionData = {
        pv: [],
        pv_adjusted: [],
        load: [],
        battery: [],
        buy_price: [],
        sell_price: [],
        buy_price_adjusted: [],
        sell_price_adjusted: []
      };
    },
    readFromCookies() {
      const toggleDataTypeCookieData = getToggleDataTypeState();
      if (toggleDataTypeCookieData && toggleDataTypeCookieData.chartToggleDataTypeValue) {
        this.chartToggleDataTypeSelectedOption = toggleDataTypeCookieData.chartToggleDataTypeValue;
      }
      
      const toggleGroupingCookieData = getToggleGroupingState();
      if (toggleGroupingCookieData && toggleGroupingCookieData.chartToggleGroupingValue) {
        this.chartToggleGroupingSelectedOption = toggleGroupingCookieData.chartToggleGroupingValue;
      }

      // Load prediction evaluation state
      const predictionEvalState = getPredictionEvaluationState();
      if (predictionEvalState && predictionEvalState.isEnabled !== undefined) {
        this.isEvalEnabled = predictionEvalState.isEnabled;
      }
      
      const powerChartLegendsCookieData = getPowerChartLegendsState();
      if (powerChartLegendsCookieData && powerChartLegendsCookieData.powerHiddenLegends) {
        this.powerChartHiddenLegends = JSON.parse(powerChartLegendsCookieData.powerHiddenLegends);
        this.powerChartHiddenLegends = this.powerChartHiddenLegends.filter(item => item != null);
      }
      
      const priceChartLegendsCookieData = getPriceChartLegendsState();
      if (priceChartLegendsCookieData && priceChartLegendsCookieData.priceHiddenLegends) {
        this.priceChartHiddenLegends = JSON.parse(priceChartLegendsCookieData.priceHiddenLegends);
        this.priceChartHiddenLegends = this.priceChartHiddenLegends.filter(item => item != null);
      }
      
      const devModeState = getDevModeState();
      if (devModeState.isDevMode !== undefined) {
        this.subDataUrl = devModeState.isDevMode ? "/dev-api/api/v1.1/data" : "/api/v1.1/data";
      }
      
      this.userPreferences = getUserPreferences();
      this.isActionReasonsEnabled = this.userPreferences.actionReasons;
      this.isBmsSimulatorEnabled = this.userPreferences.bmsSimulator;
      
      const powerChartLogScaleState = getPowerChartLogScaleState();
      if (powerChartLogScaleState && powerChartLogScaleState.isLogScale !== undefined) {
        this.powerChartLogScale = powerChartLogScaleState.isLogScale;
      }
      
      const priceChartLogScaleState = getPriceChartLogScaleState();
      if (priceChartLogScaleState && priceChartLogScaleState.isLogScale !== undefined) {
        this.priceChartLogScale = priceChartLogScaleState.isLogScale;
      }
      
      const socChartLogScaleState = getSocChartLogScaleState();
      if (socChartLogScaleState && socChartLogScaleState.isLogScale !== undefined) {
        this.socChartLogScale = socChartLogScaleState.isLogScale;
      }
    },
    handlePowerChartHiddenLegendsUpdated(hiddenLegends) {
      // Update the power chart hidden legends
      this.powerChartHiddenLegends = hiddenLegends.filter(item => item != null);
      // Save to cookie
      setPowerChartLegendsState({ powerHiddenLegends: JSON.stringify(this.powerChartHiddenLegends) });
    },
    handlePriceChartHiddenLegendsUpdated(hiddenLegends) {
      // Update the price chart hidden legends
      this.priceChartHiddenLegends = hiddenLegends.filter(item => item != null);
      // Save to cookie
      setPriceChartLegendsState({ priceHiddenLegends: JSON.stringify(this.priceChartHiddenLegends) });
    },
    togglePredictionEvaluation() {
      this.isEvalEnabled = !this.isEvalEnabled;
      setPredictionEvaluationState({ isEnabled: this.isEvalEnabled });
    },
    saveUserPreferences() {
      setUserPreferences(this.userPreferences);
      this.isActionReasonsEnabled = this.userPreferences.actionReasons;
      this.isBmsSimulatorEnabled = this.userPreferences.bmsSimulator;
      // Remove LocalVolt (RX) price series when preference is unchecked
      if (!this.userPreferences.priceActualBuySellRX) {
        this.chartDataPriceActual = this.chartDataPriceActual.filter(
          item => item.group !== 'localvolt_price_actual'
        );
        delete this.actualData['localvolt_buy_price'];
        delete this.actualData['localvolt_sell_price'];
        delete this.actualData['localvolt_time_labels'];
      }
      this.$forceUpdate();
    },
    resetPreferences() {
      this.userPreferences = getDefaultUserPreferences();
      setUserPreferences(this.userPreferences);
      this.isActionReasonsEnabled = this.userPreferences.actionReasons;
      this.isBmsSimulatorEnabled = this.userPreferences.bmsSimulator;
      this.$forceUpdate();
    },
    handleLegacyDesignToggle(event) {
      const useLegacy = event.target.checked;
      if (!useLegacy) return;
      setDetailPageVersion({ version: 'legacy' });
      this.$router.push({ name: 'DetailPageLegacy', query: this.$route.query });
    },
    togglePowerChartLogScale() {
      this.powerChartLogScale = !this.powerChartLogScale;
      setPowerChartLogScaleState({ isLogScale: this.powerChartLogScale });
    },
    setPowerChartLogScale(isLogScale) {
      this.powerChartLogScale = isLogScale;
      setPowerChartLogScaleState({ isLogScale: isLogScale });
    },
    togglePriceChartLogScale() {
      this.priceChartLogScale = !this.priceChartLogScale;
      setPriceChartLogScaleState({ isLogScale: this.priceChartLogScale });
    },
    setPriceChartLogScale(isLogScale) {
      this.priceChartLogScale = isLogScale;
      setPriceChartLogScaleState({ isLogScale: isLogScale });
    },
    setSocChartLogScale(isLogScale) {
      this.socChartLogScale = isLogScale;
      setSocChartLogScaleState({ isLogScale: isLogScale });
    },
    async loadAndShowPlantDevices() {
      try {
        const bodyData = {
          plant_id: this.plantId,
        };
        const url = `${this.subDataUrl}/bridge/rx_data/plant/devices_info/get`;
        const response = await apiRequest('post', url, bodyData);
        
        this.plantDevicesDataMap = [];
        this.isPlantDevicesDataBoxVisible = true;

        if (response.errorCode === 0 && response.data && response.data.length > 0) {
          this.plantDevicesDataMap = response.data;
          if (this.userPreferences.showDeviceSoHComparison) {
            this.plantDevicesDataMap.forEach((device) => {
              if (device.device_sn) {
                this.fetchDeviceSoHComparison(device.device_sn);
              }
            });
          }
        }
      } catch (error) {
        console.error('Error fetching plant devices data:', error);
      }
    },
    async fetchAiDeviceHealth(device_sn) {
      try {
        const url = `${this.subDataUrl}/proxy/ai_control/device/health`;
        const response = await apiRequest('post', url, { device_sn });
        if (response.status === 'success' && response.data) {
          return {
            device_soh: response.data.device_soh,
            device_rte: response.data.device_rte
          };
        }
        return null;
      } catch (error) {
        console.error('Error fetching AI device health:', error);
        return null;
      }
    },
    async fetchRxBatteryStatus(device_sn) {
      try {
        const url = `${this.subDataUrl}/proxy/rx/device/battery/get_status`;
        const response = await apiRequest('post', url, { deviceSn: device_sn });
        if (response.errorCode === 0 && response.data) {
          const d = response.data;
          return {
            bmsSoh: d.bmsSoh != null ? d.bmsSoh : d.rackSoh,
            rackSoh: d.rackSoh
          };
        }
        return null;
      } catch (error) {
        console.error('Error fetching RX battery status:', error);
        return null;
      }
    },
    async fetchDeviceSoHComparison(device_sn) {
      if (!device_sn) return;
      this.deviceSoHComparisonMap[device_sn] = {
        ai: null,
        rx: null,
        loading: true,
        error: null
      };
      const [ai, rx] = await Promise.all([
        this.fetchAiDeviceHealth(device_sn),
        this.fetchRxBatteryStatus(device_sn)
      ]);
      this.deviceSoHComparisonMap[device_sn] = {
        ai,
        rx,
        loading: false,
        error: null
      };
    },
    // Data fetching methods (similar to DetailPage but with date parameter)
    async fetchActualDataFromRedx(date, updateDirectly = true) {
      try {
        const bodyData = {
          plantId: this.plantId,
          logDate: date.replace(/-/g, '_'),
        };
        const url = `${this.subDataUrl}/proxy/rx/plant/get_daily_data`;
        const response = await apiRequest('post', url, bodyData);
      
        if (response.errorCode === 0 && response.data && response.data.length > 0) {
          const actualPower = response.data;
          const seriesLabels = {
            batP: 'Actual Battery Power',
            gridP: 'Actual Grid Power',
            loadP: 'Actual Load Power',
            ppv: 'Actual PV Power',
            soc: 'Actual SoC'
          };
          const seriesColors = {
            batP: `rgba(200, 20, 150, ${this.opacityLevelActual})`,
            gridP: `rgba(54, 162, 235, ${this.opacityLevelActual})`,
            loadP: `rgba(153, 102, 255, ${this.opacityLevelActual})`,
            ppv: `rgba(75, 192, 50, ${this.opacityLevelActual})`,
            soc: `rgba(64, 224, 208, ${this.opacityLevelActual})`,
          };
          
          const labels = this.prepareLabels(5, 288);
          const batPData = new Array(288).fill(null);
          const gridPData = new Array(288).fill(null);
          const loadPData = new Array(288).fill(null);
          const ppvData = new Array(288).fill(null);
          const socData = new Array(288).fill(null);

          actualPower.forEach((dataPoint) => {
            const { batP, gridP, loadP, ppv, soc, logTime } = dataPoint;
            const index = labels.indexOf(logTime);
            if (index !== -1) {
              // Round all values to 2 decimal places
              batPData[index] = batP != null ? this.roundToTwoDecimals(batP) : null;
              gridPData[index] = gridP != null ? this.roundToTwoDecimals(gridP) : null;
              loadPData[index] = loadP != null ? this.roundToTwoDecimals(loadP) : null;
              ppvData[index] = ppv != null ? this.roundToTwoDecimals(ppv) : null;
              socData[index] = soc != null ? this.roundToTwoDecimals(soc) : null;
            }
          });

          const powerData = [
            { group: 'battery_actual', name: seriesLabels.batP, values: batPData, color: seriesColors.batP, labels: labels },
            { group: 'grid_actual', name: seriesLabels.gridP, values: gridPData, color: seriesColors.gridP, labels: labels },
            { group: 'load_actual', name: seriesLabels.loadP, values: loadPData, color: seriesColors.loadP, labels: labels },
            { group: 'pv_actual', name: seriesLabels.ppv, values: ppvData, color: seriesColors.ppv, labels: labels },
          ];
          
          const socDataArray = [{ group: 'soc_actual', name: seriesLabels.soc, values: socData, color: seriesColors.soc, labels: labels }];

          if (updateDirectly) {
            this.chartDataPowerActual = powerData;
            this.chartDataSocActual = socDataArray;
            this.actualData['pv'] = ppvData;
            this.actualData['load'] = loadPData;
            this.actualData['battery'] = batPData;
          }
          
          return {
            power: powerData,
            soc: socDataArray,
            raw: { pv: ppvData, load: loadPData, battery: batPData }
          };
        }
        return { power: [], soc: [], raw: {} };
      } catch (error) {
        console.error('Error fetching actual data:', error);
        return { power: [], soc: [], raw: {} };
      }
    },
    async fetchPriceActualData(date, updateDirectly = true) {
      try {
        const bodyData = {
          date: date,
          plant_id: this.plantId,
        };
        const url = `${this.subDataUrl}/price_actual/get`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode === 0 && response.data) {
          const actual_price_values = response.data;
          const actualPriceBuyValues = actual_price_values.price_buy_values || [];
          const actualPriceSellValues = actual_price_values.price_sell_values || [];
          const dataTypes = actual_price_values.data_types || [];
          const labels = this.prepareLabels(5, 288);

          let unreliableBuyPoints = [];
          let unreliableSellPoints = [];
          if (dataTypes.length > 0) {
            dataTypes.forEach((dataType, index) => {
              if (dataType === 1) {
                unreliableBuyPoints.push(actualPriceBuyValues[index]);
                unreliableSellPoints.push(actualPriceSellValues[index]);
              } else {
                unreliableBuyPoints.push(null);
                unreliableSellPoints.push(null);
              }
            });
          }

          const masked = this.maskActualPriceValuesAfterCurrentTime(
            date,
            labels,
            actualPriceBuyValues,
            actualPriceSellValues,
            unreliableBuyPoints,
            unreliableSellPoints
          );

          const priceData = [
            {
              group: 'price_actual',
              name: 'Actual Price Buy',
              values: masked.buyValues,
              color: `rgba(153, 102, 255, ${this.opacityLevelActual})`,
              labels: labels,
            },
            {
              group: 'price_actual',
              name: 'Actual Price Sell',
              values: masked.sellValues,
              color: `rgba(255, 159, 64, ${this.opacityLevelActual})`,
              labels: labels,
            }
          ];

          if (dataTypes.length > 0) {
            priceData.push({
              group: 'price_actual_warnings',
              name: 'Last Known Buy Price 🚩 ',
              values: masked.unreliableBuy,
              color: 'rgba(255, 0, 0, 0.8)',
              labels: labels,
              type: 'scatter',
              marker: {
                symbol: 'triangle-up',
                size: 8,
                color: 'rgba(255, 0, 0, 0.8)'
              },
            });
            priceData.push({
              group: 'price_actual_warnings',
              name: 'Last Known Sell Price 🚩 ',
              values: masked.unreliableSell,
              color: 'rgba(255, 0, 0, 0.8)',
              labels: labels,
              marker: {
                symbol: 'triangle-up',
                size: 8,
                color: 'rgba(255, 0, 0, 0.8)'
              },
            });
          }

          if (updateDirectly) {
            this.chartDataPriceActual = priceData;
            this.actualData['buy_price'] = masked.buyValues;
            this.actualData['sell_price'] = masked.sellValues;
          }
          
          return { actual: priceData, raw: { buy: masked.buyValues, sell: masked.sellValues } };
        }
        return { actual: [], raw: {} };
      } catch (error) {
        console.error('Error fetching price actual data:', error);
        return { actual: [], raw: {} };
      }
    },
    async fetchLocalVoltPriceDataFromRedxProxy() {
      try {
        const bodyData = {
          date: this.selectedDate,
          plantId: this.plantId,
        };

        // Remove all items with group 'localvolt_price_actual'
        this.chartDataPriceActual = this.chartDataPriceActual.filter(
          item => item.group !== 'localvolt_price_actual'
        );

        const url = `${this.subDataUrl}/proxy/rx/localvoltapi/localvolt/get_price`;
        const response = await apiRequest('post', url, bodyData);

        if (response.errorCode === 0 && response.data) {
          const localVoltData = response.data || [];

          // Filter data to only include items where intervalEnd date matches selectedDate
          const filteredLocalVoltData = localVoltData.filter(item => {
            if (item && item.intervalEnd) {
              const intervalEndDate = item.intervalEnd.split(' ')[0]; // Extract date part "YYYY-MM-DD"
              return intervalEndDate === this.selectedDate;
            }
            return false;
          });

          // Generate complete 5-minute interval timeline for 24 hours (288 intervals)
          const generateTimeIntervals = () => {
            const intervals = [];
            for (let hour = 0; hour < 24; hour++) {
              for (let minute = 0; minute < 60; minute += 5) {
                const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                intervals.push(timeStr);
              }
            }
            return intervals;
          };

          const allTimeIntervals = generateTimeIntervals();

          // Create a map of existing data by time (using filtered data)
          const dataMap = new Map();
          filteredLocalVoltData.forEach(item => {
            if (item && item.intervalEnd) {
              const timeStr = item.intervalEnd.split(' ')[1].substring(0, 5); // "00:05"
              dataMap.set(timeStr, {
                earningsFlexUp: item.earningsFlexUp || 0,
                costsFlexUp: item.costsFlexUp || 0
              });
            }
          });

          // Fill arrays with data or 0 for missing intervals
          const localVoltBuyValues = [];
          const localVoltSellValues = [];
          const localVoltTimeLabels = [];

          allTimeIntervals.forEach(timeStr => {
            const data = dataMap.get(timeStr);
            // Convert cents to AUD (divide by 100) and round to 4 decimal places
            const buyValue = data ? parseFloat((data.costsFlexUp / 100).toFixed(4)) : 0;
            const sellValue = data ? parseFloat((data.earningsFlexUp / 100).toFixed(4)) : 0;

            localVoltBuyValues.push(buyValue);
            localVoltSellValues.push(sellValue);
            localVoltTimeLabels.push(timeStr);
          });

          // Remove all items with group 'localvolt_price_actual'
          this.chartDataPriceActual = this.chartDataPriceActual.filter(
            item => item.group !== 'localvolt_price_actual'
          );

          // Push LocalVolt data to the same chartDataPriceActual array
          this.chartDataPriceActual.push(
            {
              group: 'localvolt_price_actual',
              name: 'Actual Price Buy (RX)',
              values: localVoltBuyValues,
              color: `rgba(54, 162, 235, ${this.opacityLevelActual})`, // Blue
              labels: localVoltTimeLabels,
            },
            {
              group: 'localvolt_price_actual',
              name: 'Actual Price Sell (RX)',
              values: localVoltSellValues,
              color: `rgba(255, 99, 132, ${this.opacityLevelActual})`, // Red color
              labels: localVoltTimeLabels,
            }
          );

          // Store in actualData for reference
          this.actualData['localvolt_buy_price'] = localVoltBuyValues;
          this.actualData['localvolt_sell_price'] = localVoltSellValues;
          this.actualData['localvolt_time_labels'] = localVoltTimeLabels;
        }
      } catch (error) {
        // Silent catch: failed fetches do not surface to the user
      }
    },
    async fetchPvPredictions(date, updateDirectly = true) {
      try {
        const bodyData = {
          model_date: date,
          plant_id: this.plantId,
        };
        const url = `${this.subDataUrl}/pv_prediction/get`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode == 0 && response.data != null && response.data.length > 0) {
          const predictions = response.data;
          const modelPvData = predictions[0].model_pv;
          const decodedPv = this.decodeModelData(modelPvData);
          const modelAdjustedPvData = predictions[0].model_adjusted_pv;
          const decodedAdjustedPv = this.decodeModelData(modelAdjustedPvData);
          const labels = this.prepareLabels(5, 288);

          const pvData = [{
            group: 'pv_prediction',
            name: 'Predicted PV Power',
            values: decodedPv || [],
            color: `rgba(75, 192, 50, ${this.opacityLevelPrediction})`,
            labels: labels,
          }];

          if (decodedAdjustedPv && decodedAdjustedPv.length > 0) {
            pvData.push({
              group: 'pv_prediction_adjusted',
              name: 'Predicted PV Power (adjusted)',
              values: decodedAdjustedPv,
              color: `rgba(75, 192, 50, ${this.opacityLevelPrediction})`,
              labels: labels,
            });
          }

          if (updateDirectly) {
            // Remove existing PV predictions first
            this.chartDataPowerPrediction = this.chartDataPowerPrediction.filter(
              ds => !ds.group.includes('pv_prediction')
            );
            this.chartDataPowerPrediction = [...this.chartDataPowerPrediction, ...pvData];
            this.predictionData['pv'] = decodedPv || [];
            this.predictionData['pv_adjusted'] = decodedAdjustedPv || [];
          }
          
          return pvData;
        }
        return [];
      } catch (error) {
        console.error('Error fetching PV predictions:', error);
        return [];
      }
    },
    async fetchPricePredictions(date, updateDirectly = true) {
      try {
        const bodyData = {
          model_date: date,
          plant_id: this.plantId,
        };
        const url = `${this.subDataUrl}/price_prediction/get`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode === 0 && response.data && response.data.length > 0) {
          const predictions = response.data;
          const modelPriceBuyData = predictions[0].model_price_buy;
          const modelPriceSellData = predictions[0].model_price_sell;
          const modelAdjustedPriceBuyData = predictions[0].model_adjusted_price_buy;
          const modelAdjustedPriceSellData = predictions[0].model_adjusted_price_sell;
          const decodedModelPriceBuy = this.decodeModelData(modelPriceBuyData);
          const decodedModelPriceSell = this.decodeModelData(modelPriceSellData);
          const decodedModelAdjustedPriceBuy = this.decodeModelData(modelAdjustedPriceBuyData);
          const decodedModelAdjustedPriceSell = this.decodeModelData(modelAdjustedPriceSellData);
          const labels = this.prepareLabels(5, 288);

          const priceData = [
            {
              group: 'price_prediction',
              name: 'Predicted Price Buy',
              values: decodedModelPriceBuy || [],
              color: `rgba(153, 102, 255, ${this.opacityLevelPrediction})`,
              labels: labels,
            },
            {
              group: 'price_prediction',
              name: 'Predicted Price Sell',
              values: decodedModelPriceSell || [],
              color: `rgba(255, 159, 64, ${this.opacityLevelPrediction})`,
              labels: labels,
            },
          ];

          if (decodedModelAdjustedPriceBuy && decodedModelAdjustedPriceBuy.length > 0) {
            priceData.push({
              group: 'price_prediction_adjusted',
              name: 'Predicted Price Buy (adjusted)',
              values: decodedModelAdjustedPriceBuy,
              color: `rgba(153, 102, 255, ${this.opacityLevelPrediction})`,
              labels: labels,
            });
          }

          if (decodedModelAdjustedPriceSell && decodedModelAdjustedPriceSell.length > 0) {
            priceData.push({
              group: 'price_prediction_adjusted',
              name: 'Predicted Price Sell (adjusted)',
              values: decodedModelAdjustedPriceSell,
              color: `rgba(255, 159, 64, ${this.opacityLevelPrediction})`,
              labels: labels,
            });
          }

          if (updateDirectly) {
            this.chartDataPricePrediction = priceData;
            this.predictionData['buy_price'] = decodedModelPriceBuy || [];
            this.predictionData['sell_price'] = decodedModelPriceSell || [];
            this.predictionData['buy_price_adjusted'] = decodedModelAdjustedPriceBuy || [];
            this.predictionData['sell_price_adjusted'] = decodedModelAdjustedPriceSell || [];
          }
          
          return priceData;
        }
        return [];
      } catch (error) {
        console.error('Error fetching price predictions:', error);
        return [];
      }
    },
    async fetchLoadPredictions(date, updateDirectly = true) {
      try {
        const bodyData = {
          model_date: date,
          plant_id: this.plantId,
        };
        const url = `${this.subDataUrl}/load_prediction/get`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode === 0 && response.data && response.data.length > 0) {
          const predictions = response.data;
          const modelPredictionLoadData = predictions[0].model_prediction_load;
          const modelPredictionLowerData = predictions[0].model_prediction_lower;
          const modelPredictionUpperData = predictions[0].model_prediction_upper;
          const modelAdjustedPredictionLoadData = predictions[0].model_prediction_load_adjusted;
          const decodedModelPredictionLoad = this.decodeModelData(modelPredictionLoadData);
          const decodedModelPredictionLower = this.decodeModelData(modelPredictionLowerData);
          const decodedModelPredictionUpper = this.decodeModelData(modelPredictionUpperData);
          const decodedAdjustedPredictionLoad = this.decodeModelData(modelAdjustedPredictionLoadData);
          const labels = this.prepareLabels(5, 288);

          const loadData = [
            {
              group: 'load_prediction',
              name: 'Predicted Load',
              values: decodedModelPredictionLoad || [],
              color: `rgba(153, 102, 255, ${this.opacityLevelPrediction})`,
              labels: labels,
            },
            {
              group: 'load_prediction',
              name: 'Predicted Load Lower',
              values: decodedModelPredictionLower || [],
              color: `rgba(153, 102, 255, ${this.opacityLevelPrediction})`,
              labels: labels,
            },
            {
              group: 'load_prediction',
              name: 'Predicted Load Upper',
              values: decodedModelPredictionUpper || [],
              color: `rgba(153, 102, 255, ${this.opacityLevelPrediction})`,
              labels: labels,
            }
          ];

          if (decodedAdjustedPredictionLoad && decodedAdjustedPredictionLoad.length > 0) {
            loadData.push({
              group: 'load_prediction_adjusted',
              name: 'Predicted Load (adjusted - hourly updated)',
              values: decodedAdjustedPredictionLoad,
              color: `rgba(153, 102, 255, ${this.opacityLevelPrediction})`,
              labels: labels,
            });
          }

          if (updateDirectly) {
            // Remove existing load predictions first
            this.chartDataPowerPrediction = this.chartDataPowerPrediction.filter(
              ds => !ds.group.includes('load_prediction')
            );
            this.chartDataPowerPrediction = [...this.chartDataPowerPrediction, ...loadData];
            this.predictionData['load'] = decodedModelPredictionLoad || [];
          }
          
          return loadData;
        }
        return [];
      } catch (error) {
        console.error('Error fetching load predictions:', error);
        return [];
      }
    },
    async fetchBatteryActionPredictions(date, updateDirectly = true) {
      try {
        const bodyData = {
          date: date,
          plant_id: this.plantId,
        };
        const url = `${this.subDataUrl}/battery_actions/get`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode === 0 && response.data && response.data.length > 0) {
          const battery_actions = response.data;
          const actionPowerData = battery_actions[0].action_power;
          const decodedActionPower = this.decodeModelData(actionPowerData);
          const labels = this.prepareLabels(5, 288);

          const batteryData = [{
            group: 'battery_actions_prediction',
            name: 'Predicted Battery Actions',
            values: decodedActionPower || [],
            color: `rgba(200, 20, 150, ${this.opacityLevelPrediction})`,
            labels: labels,
          }];

          if (updateDirectly) {
            // Remove existing battery predictions first
            this.chartDataPowerPrediction = this.chartDataPowerPrediction.filter(
              ds => !ds.group.includes('battery_actions_prediction')
            );
            this.chartDataPowerPrediction = [...this.chartDataPowerPrediction, ...batteryData];
            this.predictionData['battery'] = decodedActionPower || [];
          }
          
          return batteryData;
        }
        return [];
      } catch (error) {
        console.error('Error fetching battery action predictions:', error);
        return [];
      }
    },
    async fetchBulkActualData(startDate, endDate) {
      try {
        const bodyData = {
          plant_id: this.plantId,
          start_date: startDate,
          end_date: endDate,
        };
        const url = `${this.subDataUrl}/bulk_data_cache_rx/plant/get`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode === 0 && response.data && response.data.data) {
          const bulkData = response.data.data;
          const processedData = {};
          
          // Process each date in the bulk response
          Object.keys(bulkData).forEach(dateKey => {
            const dateData = bulkData[dateKey];
            if (dateData && dateData.errorCode === 0 && dateData.data && dateData.data.length > 0) {
              const actualPower = dateData.data;
              const seriesLabels = {
                batP: 'Actual Battery Power',
                gridP: 'Actual Grid Power',
                loadP: 'Actual Load Power',
                ppv: 'Actual PV Power',
                soc: 'Actual SoC'
              };
              const seriesColors = {
                batP: `rgba(200, 20, 150, ${this.opacityLevelActual})`,
                gridP: `rgba(54, 162, 235, ${this.opacityLevelActual})`,
                loadP: `rgba(153, 102, 255, ${this.opacityLevelActual})`,
                ppv: `rgba(75, 192, 50, ${this.opacityLevelActual})`,
                soc: `rgba(64, 224, 208, ${this.opacityLevelActual})`,
              };
              
              const labels = this.prepareLabels(5, 288);
              const batPData = new Array(288).fill(null);
              const gridPData = new Array(288).fill(null);
              const loadPData = new Array(288).fill(null);
              const ppvData = new Array(288).fill(null);
              const socData = new Array(288).fill(null);

              actualPower.forEach((dataPoint) => {
                const { batP, gridP, loadP, ppv, soc, logTime } = dataPoint;
                const index = labels.indexOf(logTime);
                if (index !== -1) {
                  batPData[index] = batP != null ? this.roundToTwoDecimals(batP) : null;
                  gridPData[index] = gridP != null ? this.roundToTwoDecimals(gridP) : null;
                  loadPData[index] = loadP != null ? this.roundToTwoDecimals(loadP) : null;
                  ppvData[index] = ppv != null ? this.roundToTwoDecimals(ppv) : null;
                  socData[index] = soc != null ? this.roundToTwoDecimals(soc) : null;
                }
              });

              const powerData = [
                { group: 'battery_actual', name: seriesLabels.batP, values: batPData, color: seriesColors.batP, labels: labels },
                { group: 'grid_actual', name: seriesLabels.gridP, values: gridPData, color: seriesColors.gridP, labels: labels },
                { group: 'load_actual', name: seriesLabels.loadP, values: loadPData, color: seriesColors.loadP, labels: labels },
                { group: 'pv_actual', name: seriesLabels.ppv, values: ppvData, color: seriesColors.ppv, labels: labels },
              ];
              
              const socDataArray = [{ group: 'soc_actual', name: seriesLabels.soc, values: socData, color: seriesColors.soc, labels: labels }];

              processedData[dateKey] = {
                power: powerData,
                soc: socDataArray,
                raw: { pv: ppvData, load: loadPData, battery: batPData }
              };
            }
          });
          
          return processedData;
        }
        return {};
      } catch (error) {
        console.error('Error fetching bulk actual data:', error);
        return {};
      }
    },
    async fetchBulkPredictionData(startDate, endDate) {
      try {
        const bodyData = {
          plant_id: this.plantId,
          start_date: startDate,
          end_date: endDate,
        };
        const url = `${this.subDataUrl}/bulk_data_cache_rx_ai/get`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode === 0 && response.data && response.data.data) {
          const bulkData = response.data.data;
          const processedData = {};
          
          // Process each date in the bulk response
          Object.keys(bulkData).forEach(dateKey => {
            const dateData = bulkData[dateKey];
            // Skip if dateData is null (as mentioned in API docs)
            if (!dateData) {
              return;
            }
            
            const labels = this.prepareLabels(5, 288);
            const datePredictions = {
              pv: [],
              load: [],
              price: [],
              battery: [],
              priceActual: [] // Add price actual data
            };
            
            // Process Price actual data
            if (dateData.price_actual) {
              const actual_price_values = dateData.price_actual;
              const actualPriceBuyValues = actual_price_values.price_buy_values || [];
              const actualPriceSellValues = actual_price_values.price_sell_values || [];
              const dataTypes = actual_price_values.data_types || [];

              let unreliableBuyPoints = [];
              let unreliableSellPoints = [];
              if (dataTypes.length > 0) {
                dataTypes.forEach((dataType, index) => {
                  if (dataType === 1) {
                    unreliableBuyPoints.push(actualPriceBuyValues[index]);
                    unreliableSellPoints.push(actualPriceSellValues[index]);
                  } else {
                    unreliableBuyPoints.push(null);
                    unreliableSellPoints.push(null);
                  }
                });
              }

              const masked = this.maskActualPriceValuesAfterCurrentTime(
                dateKey,
                labels,
                actualPriceBuyValues,
                actualPriceSellValues,
                unreliableBuyPoints,
                unreliableSellPoints
              );

              const priceData = [
                {
                  group: 'price_actual',
                  name: 'Actual Price Buy',
                  values: masked.buyValues,
                  color: `rgba(153, 102, 255, ${this.opacityLevelActual})`,
                  labels: labels,
                },
                {
                  group: 'price_actual',
                  name: 'Actual Price Sell',
                  values: masked.sellValues,
                  color: `rgba(255, 159, 64, ${this.opacityLevelActual})`,
                  labels: labels,
                }
              ];

              if (dataTypes.length > 0) {
                priceData.push({
                  group: 'price_actual_warnings',
                  name: 'Last Known Buy Price 🚩 ',
                  values: masked.unreliableBuy,
                  color: 'rgba(255, 0, 0, 0.8)',
                  labels: labels,
                  type: 'scatter',
                  marker: {
                    symbol: 'triangle-up',
                    size: 8,
                    color: 'rgba(255, 0, 0, 0.8)'
                  },
                });
                priceData.push({
                  group: 'price_actual_warnings',
                  name: 'Last Known Sell Price 🚩 ',
                  values: masked.unreliableSell,
                  color: 'rgba(255, 0, 0, 0.8)',
                  labels: labels,
                  marker: {
                    symbol: 'triangle-up',
                    size: 8,
                    color: 'rgba(255, 0, 0, 0.8)'
                  },
                });
              }
              
              datePredictions.priceActual = priceData;
            }
            
            // Process PV predictions
            if (dateData.pv_prediction && dateData.pv_prediction.length > 0) {
              const pvPred = dateData.pv_prediction[0];
              const decodedPv = this.decodeModelData(pvPred.model_pv);
              const decodedAdjustedPv = this.decodeModelData(pvPred.model_adjusted_pv);
              
              const pvData = [{
                group: 'pv_prediction',
                name: 'Predicted PV Power',
                values: decodedPv || [],
                color: `rgba(75, 192, 50, ${this.opacityLevelPrediction})`,
                labels: labels,
              }];
              
              if (decodedAdjustedPv && decodedAdjustedPv.length > 0) {
                pvData.push({
                  group: 'pv_prediction_adjusted',
                  name: 'Predicted PV Power (adjusted)',
                  values: decodedAdjustedPv,
                  color: `rgba(75, 192, 50, ${this.opacityLevelPrediction})`,
                  labels: labels,
                });
              }
              
              datePredictions.pv = pvData;
            }
            
            // Process Load predictions
            if (dateData.load_prediction && dateData.load_prediction.length > 0) {
              const loadPred = dateData.load_prediction[0];
              const decodedLoad = this.decodeModelData(loadPred.model_prediction_load);
              const decodedLower = this.decodeModelData(loadPred.model_prediction_lower);
              const decodedUpper = this.decodeModelData(loadPred.model_prediction_upper);
              const decodedAdjustedLoad = this.decodeModelData(loadPred.model_prediction_load_adjusted);
              
              const loadData = [
                {
                  group: 'load_prediction',
                  name: 'Predicted Load',
                  values: decodedLoad || [],
                  color: `rgba(153, 102, 255, ${this.opacityLevelPrediction})`,
                  labels: labels,
                },
                {
                  group: 'load_prediction',
                  name: 'Predicted Load Lower',
                  values: decodedLower || [],
                  color: `rgba(153, 102, 255, ${this.opacityLevelPrediction})`,
                  labels: labels,
                },
                {
                  group: 'load_prediction',
                  name: 'Predicted Load Upper',
                  values: decodedUpper || [],
                  color: `rgba(153, 102, 255, ${this.opacityLevelPrediction})`,
                  labels: labels,
                }
              ];
              
              if (decodedAdjustedLoad && decodedAdjustedLoad.length > 0) {
                loadData.push({
                  group: 'load_prediction_adjusted',
                  name: 'Predicted Load (adjusted - hourly updated)',
                  values: decodedAdjustedLoad,
                  color: `rgba(153, 102, 255, ${this.opacityLevelPrediction})`,
                  labels: labels,
                });
              }
              
              datePredictions.load = loadData;
            }
            
            // Process Price predictions
            if (dateData.price_prediction && dateData.price_prediction.length > 0) {
              const pricePred = dateData.price_prediction[0];
              const decodedBuy = this.decodeModelData(pricePred.model_price_buy);
              const decodedSell = this.decodeModelData(pricePred.model_price_sell);
              const decodedAdjustedBuy = this.decodeModelData(pricePred.model_adjusted_price_buy);
              const decodedAdjustedSell = this.decodeModelData(pricePred.model_adjusted_price_sell);
              
              const priceData = [
                {
                  group: 'price_prediction',
                  name: 'Predicted Price Buy',
                  values: decodedBuy || [],
                  color: `rgba(153, 102, 255, ${this.opacityLevelPrediction})`,
                  labels: labels,
                },
                {
                  group: 'price_prediction',
                  name: 'Predicted Price Sell',
                  values: decodedSell || [],
                  color: `rgba(255, 159, 64, ${this.opacityLevelPrediction})`,
                  labels: labels,
                },
              ];
              
              if (decodedAdjustedBuy && decodedAdjustedBuy.length > 0) {
                priceData.push({
                  group: 'price_prediction_adjusted',
                  name: 'Predicted Price Buy (adjusted)',
                  values: decodedAdjustedBuy,
                  color: `rgba(153, 102, 255, ${this.opacityLevelPrediction})`,
                  labels: labels,
                });
              }
              
              if (decodedAdjustedSell && decodedAdjustedSell.length > 0) {
                priceData.push({
                  group: 'price_prediction_adjusted',
                  name: 'Predicted Price Sell (adjusted)',
                  values: decodedAdjustedSell,
                  color: `rgba(255, 159, 64, ${this.opacityLevelPrediction})`,
                  labels: labels,
                });
              }
              
              datePredictions.price = priceData;
            }
            
            processedData[dateKey] = datePredictions;
          });
          
          return processedData;
        }
        return {};
      } catch (error) {
        console.error('Error fetching bulk prediction data:', error);
        return {};
      }
    },
    async fetchBulkLocalVoltSummary(startDate, endDate) {
      try {
        const bodyData = {
          plant_id: this.plantId,
          start_date: startDate,
          end_date: endDate,
        };
        const url = `${this.subDataUrl}/bulk_data_cache_rx_localvolt_summary/get`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode === 0 && response.data && response.data.data) {
          const bulkData = response.data.data;
          let totalDailyBalance = 0;
          let totalGridImport = 0;
          
          // Process each date in the bulk response
          Object.keys(bulkData).forEach(dateKey => {
            const dateData = bulkData[dateKey];
            if (dateData && dateData.errorCode === 0 && dateData.data) {
              totalDailyBalance += parseFloat(dateData.data['dailyBalance'] || 0);
              totalGridImport += parseFloat(dateData.data['gridImport'] || 0);
            }
          });
          
          return {
            dailyBalance: totalDailyBalance,
            gridImport: totalGridImport
          };
        }
        return { dailyBalance: 0, gridImport: 0 };
      } catch (error) {
        console.error('Error fetching bulk localvolt summary:', error);
        return { dailyBalance: 0, gridImport: 0 };
      }
    },
    async getNetCostAfterServiceFees(date) {
      try {
        const bodyData = {
          plantId: this.plantId,
          date: date || this.selectedDate,
        };
        const url = `${this.subDataUrl}/proxy/rx/localvoltapi/localvolt/get_summary`;
        const response = await apiRequest('post', url, bodyData);
        if (response.errorCode === 0 && response.data) {
          return {
            dailyBalance: parseFloat(response.data['dailyBalance'] || 0),
            gridImport: parseFloat(response.data['gridImport'] || 0)
          };
        } else {
          return { dailyBalance: 0, gridImport: 0 };
        }
      } catch (error) {
        console.log('Error fetching net cost after service fees:', error);
        return { dailyBalance: 0, gridImport: 0 };
      }
    },
    async getPerformanceMetricsData() {
      try {
        // Initialize metrics data
        this.performanceMetricsData = {
          totalConsumption: 0,
          gridImport: 0,
          usageCost: 0,
          feedInCost: 0,
          originalCost: 0,
          costWithOptimisation: 0,
          originalCostPerkWh: 0,
          costWithOptimisationPerkWh: 0,
          savings: 0,
          netCostAfterServiceFees: 0,
        };

        // Check if we need to aggregate across multiple dates
        const datesToFetch = this.dateRangeArray.length > 1 ? this.dateRangeArray : [this.selectedDate];
        
        // Initialize sums
        let totalConsumption = 0;
        let gridImport = 0;
        let usageCost = 0;
        let feedInCost = 0;
        let originalCost = 0;
        let costWithOptimisation = 0;
        let netCostAfterServiceFees = 0;

        // Use bulk API for multiple dates, single API for one date
        if (datesToFetch.length === 1) {
          // Single date: use existing logic
          const date = datesToFetch[0];
          try {
            const bodyData = {
              date: date,
              plant_id: this.plantId,
            };
            const url = `${this.subDataUrl}/performance_metrics/get`;
            
            const [metricsResponse, localVoltData] = await Promise.all([
              apiRequest('post', url, bodyData),
              this.getNetCostAfterServiceFees(date)
            ]);
            
            if (metricsResponse.errorCode == 0 && metricsResponse.data != null && metricsResponse.data.length > 0) {
              const data = metricsResponse.data[0];
              totalConsumption = parseFloat(data['total_consumption'] || 0);
              usageCost = parseFloat(data['usage_cost'] || 0);
              feedInCost = parseFloat(data['feed_in_cost'] || 0);
              originalCost = parseFloat(data['original_cost'] || 0);
              costWithOptimisation = parseFloat(data['cost_with_optimisation'] || 0);
            }
            netCostAfterServiceFees = localVoltData.dailyBalance;
            gridImport = localVoltData.gridImport;
          } catch (error) {
            console.error(`Error fetching performance metrics for date ${date}:`, error);
          }
        } else {
          // Multiple dates: use bulk APIs
          const startDate = datesToFetch[0];
          const endDate = datesToFetch[datesToFetch.length - 1];
          
          try {
            const bodyData = {
              start_date: startDate,
              end_date: endDate,
              plant_id: this.plantId,
            };
            const url = `${this.subDataUrl}/performance_metrics/bulk/get`;
            
            // Fetch bulk performance metrics and bulk localvolt summary in parallel
            const [bulkMetricsResponse, bulkLocalVoltData] = await Promise.all([
              apiRequest('post', url, bodyData),
              this.fetchBulkLocalVoltSummary(startDate, endDate)
            ]);
            
            // Process bulk metrics response
            if (bulkMetricsResponse && bulkMetricsResponse.errorCode === 0 && bulkMetricsResponse.data && Array.isArray(bulkMetricsResponse.data)) {
              bulkMetricsResponse.data.forEach(item => {
                if (item.total_consumption !== undefined && item.original_cost !== undefined && item.cost_with_optimisation !== undefined) {
                  totalConsumption += parseFloat(item.total_consumption || 0);
                  usageCost += parseFloat(item.usage_cost || 0);
                  feedInCost += parseFloat(item.feed_in_cost || 0);
                  originalCost += parseFloat(item.original_cost || 0);
                  costWithOptimisation += parseFloat(item.cost_with_optimisation || 0);
                }
              });
            }
            
            // Use aggregated data from bulk localvolt summary
            netCostAfterServiceFees = bulkLocalVoltData.dailyBalance;
            gridImport = bulkLocalVoltData.gridImport;
          } catch (error) {
            console.error(`Error fetching bulk performance metrics for date range ${startDate} to ${endDate}:`, error);
          }
        }

        // Set aggregated values
        this.performanceMetricsData['totalConsumption'] = totalConsumption;
        this.performanceMetricsData['gridImport'] = gridImport;
        this.performanceMetricsData['usageCost'] = usageCost;
        this.performanceMetricsData['feedInCost'] = feedInCost;
        this.performanceMetricsData['originalCost'] = originalCost;
        this.performanceMetricsData['costWithOptimisation'] = costWithOptimisation;
        this.performanceMetricsData['netCostAfterServiceFees'] = netCostAfterServiceFees;

        // Calculate derived values
        this.performanceMetricsData['originalCostPerkWh'] = totalConsumption > 0 
          ? (originalCost / totalConsumption).toFixed(2)
          : 0;
        this.performanceMetricsData['costWithOptimisationPerkWh'] = totalConsumption > 0
          ? (costWithOptimisation / totalConsumption).toFixed(4)
          : 0;
        this.performanceMetricsData['savings'] = (originalCost - costWithOptimisation).toFixed(2);
      } catch (error) {
        console.error('Error fetching performance metrics:', error);
      }
    },
    async getTimezoneData() {
      try {
        const bodyData = {
          plant_id: this.plantId,
        };
        const url = `${this.subDataUrl}/bridge/rx_data/plant/plant_info/get`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode === 0 && response.data && response.data.length > 0) {
          // Timezone data is passed as prop, but we can update if needed
        }
      } catch (error) {
        console.error('Error fetching timezone data:', error);
      }
    },
    filterDatasetsByPreferences(datasets) {
      if (!datasets || !Array.isArray(datasets)) return [];
      
      return datasets.filter(dataset => {
        const name = dataset.name.toLowerCase();
        const group = dataset.group.toLowerCase();
        
        if (name.includes('predicted battery actions') && !name.includes('oracle')) {
          return this.userPreferences.predictedBatteryActions;
        }
        if (name.includes('oracle battery actions')) {
          return this.userPreferences.predictedBatteryActionsOracle;
        }
        if (group.includes('price_prediction') || name.includes('predicted price')) {
          return this.userPreferences.pricePrediction;
        }
        if (name.includes('predicted pv power') && !name.includes('adjusted')) {
          return this.userPreferences.pvPredictionNonAdjusted;
        }
        if (name.includes('predicted load lower')) {
          return this.userPreferences.loadPredictionLowerBound;
        }
        if (name.includes('predicted load upper')) {
          return this.userPreferences.loadPredictionUpperBound;
        }
        if (name.includes('predicted load') && name.includes('hourly')) {
          return this.userPreferences.predictedLoadHourly;
        }
        if (name.includes('actual price') && name.includes('(rx)')) {
          return this.userPreferences.priceActualBuySellRX;
        }
        if (name.includes('predicted grid power')) {
          return this.userPreferences.predictedGridPower;
        }
        
        return true;
      });
    },
    getPowerDatasets() {
      const getGroupOrder = (groupName) => {
        const name = groupName.toLowerCase();
        if (name.includes('pv')) return 1;
        if (name.includes('load')) return 2;
        if (name.includes('battery')) return 3;
        if (name.includes('grid')) return 4;
        return 5;
      };

      const sortDatasets = (datasets) => {
        return datasets.sort((a, b) => {
          const orderA = getGroupOrder(a.group);
          const orderB = getGroupOrder(b.group);
          return orderA - orderB;
        });
      };

      if (this.chartToggleGroupingSelectedOption === "All") {
        if (this.chartToggleDataTypeSelectedOption === "Predicted") {
          const datasets = Array.isArray(this.chartDataPowerPrediction) ? this.chartDataPowerPrediction : [];
          const filtered = this.filterDatasetsByPreferences(datasets);
          return sortDatasets(filtered);
        } else if (this.chartToggleDataTypeSelectedOption === "Actual") {
          const datasets = Array.isArray(this.chartDataPowerActual) ? this.chartDataPowerActual : [];
          const filtered = this.filterDatasetsByPreferences(datasets);
          return sortDatasets(filtered);
        } else if (this.chartToggleDataTypeSelectedOption === "Both") {
          const predicted = Array.isArray(this.chartDataPowerPrediction) ? this.chartDataPowerPrediction : [];
          const actual = Array.isArray(this.chartDataPowerActual) ? this.chartDataPowerActual : [];
          const combined = predicted.concat(actual);
          const filtered = this.filterDatasetsByPreferences(combined);
          return sortDatasets(filtered);
        }
      } else {
        const predicted = Array.isArray(this.chartDataPowerPrediction) ? this.chartDataPowerPrediction : [];
        const actual = Array.isArray(this.chartDataPowerActual) ? this.chartDataPowerActual : [];
        let combinedDataArray = predicted.concat(actual);
        
        if (this.chartToggleGroupingSelectedOption != "All") {
          combinedDataArray = combinedDataArray.filter(item => {
            return item['group'].toLowerCase().slice(0, this.chartToggleGroupingSelectedOption.length) === this.chartToggleGroupingSelectedOption.toLowerCase();
          });
          const filtered = this.filterDatasetsByPreferences(combinedDataArray);
          return sortDatasets(filtered);
        }
      }
      return [];
    },
    getPriceDatasets() {
      if (this.chartToggleDataTypeSelectedOption === "Predicted") {
        const datasets = Array.isArray(this.chartDataPricePrediction) ? this.chartDataPricePrediction : [];
        return this.filterDatasetsByPreferences(datasets);
      } else if (this.chartToggleDataTypeSelectedOption === "Actual") {
        const datasets = Array.isArray(this.chartDataPriceActual) ? this.chartDataPriceActual : [];
        return this.filterDatasetsByPreferences(datasets);
      } else if (this.chartToggleDataTypeSelectedOption === "Both") {
        const predicted = Array.isArray(this.chartDataPricePrediction) ? this.chartDataPricePrediction : [];
        const actual = Array.isArray(this.chartDataPriceActual) ? this.chartDataPriceActual : [];
        const combined = predicted.concat(actual);
        return this.filterDatasetsByPreferences(combined);
      }
      return [];
    },
    getSocDatasets() {
      return Array.isArray(this.chartDataSocActual) ? this.chartDataSocActual : [];
    },
    selectToggleDataTypeOption(option) {
      this.chartToggleGroupingSelectedOption = "All";
      setToggleGroupingState({chartToggleGroupingValue: this.chartToggleGroupingSelectedOption});
      this.chartToggleDataTypeSelectedOption = option;
      setToggleDataTypeState({chartToggleDataTypeValue: this.chartToggleDataTypeSelectedOption});
    },
    selectToggleGroupingOption(option) {
      this.chartToggleDataTypeSelectedOption = 'Both';
      setToggleDataTypeState({chartToggleDataTypeValue: this.chartToggleDataTypeSelectedOption});
      this.chartToggleGroupingSelectedOption = option;
      setToggleGroupingState({chartToggleGroupingValue: this.chartToggleGroupingSelectedOption});
    },
    navigateToReportPage() {
      const queryParams = { plant_id: this.plantId };
      eventBus.emit('nav-click', 2);
      this.$router.push({ name: 'Performance Report', query: queryParams });
    },
    decodeModelData(encodedData) {
      const tryDecodingChunk = (chunk) => {
        try {
          const decoded = base64.decode(chunk);
          try {
            return { success: true, data: JSON.parse(decoded) };
          } catch {
            const numericString = decoded.toString().trim();
            if (numericString.match(/^[\d.,\s]+$/)) {
              const numbers = numericString
                .split(',')
                .map(n => parseFloat(n.trim()))
                .filter(n => !isNaN(n));
              if (numbers.length > 0) {
                return { success: true, data: numbers };
              }
            }
            return { success: false };
          }
        } catch {
          return { success: false };
        }
      };
      
      const chunks = encodedData.split(/[^A-Za-z0-9+/=]/);
      let bestResult = { success: false, data: null };
      let maxLength = 0;

      for (const chunk of chunks) {
        if (chunk.length < 4) continue;
        const result = tryDecodingChunk(chunk);
        if (result.success) {
          const dataLength = Array.isArray(result.data) ? result.data.length : 1;
          if (dataLength > maxLength) {
            maxLength = dataLength;
            bestResult = result;
          }
        }
      }

      return bestResult.success ? bestResult.data : null;
    },
    handleEnvironmentChange(isDevMode) {
      this.subDataUrl = isDevMode ? "/dev-api/api/v1.1/data" : "/api/v1.1/data";
      this.triggerAllFetches();
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') {
        return '0.00';
      }
      const num = parseFloat(value);
      if (isNaN(num)) {
        return '0.00';
      }
      return num.toFixed(2);
    },
    roundToTwoDecimals(value) {
      // Round a number to 2 decimal places, returning a number (not string)
      if (value == null || isNaN(value)) return null;
      return parseFloat(parseFloat(value).toFixed(2));
    },
    getCurrentLocalDateAndTime() {
      // Use same timezone as "Local Time" in Plant Information (Intl with timezone name for consistency)
      const tz = this.timezoneDataMap && this.timezoneDataMap.timezone_name;
      if (!tz || typeof tz !== 'string') {
        return { localDateStr: null, localTimeStr: null };
      }
      const now = new Date();
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).formatToParts(now);
      const get = (type) => (parts.find(p => p.type === type) || {}).value || '';
      const localDateStr = `${get('year')}-${get('month')}-${get('day')}`;
      const localTimeStr = `${get('hour')}:${get('minute')}`;
      return { localDateStr, localTimeStr };
    },
    maskActualPriceValuesAfterCurrentTime(selectedDate, labels, buyValues, sellValues, optionalUnreliableBuy, optionalUnreliableSell) {
      const { localDateStr, localTimeStr } = this.getCurrentLocalDateAndTime();
      if (localDateStr == null || selectedDate !== localDateStr) {
        return {
          buyValues: buyValues || [],
          sellValues: sellValues || [],
          unreliableBuy: optionalUnreliableBuy || [],
          unreliableSell: optionalUnreliableSell || []
        };
      }
      const len = labels.length;
      const maskedBuy = (buyValues || []).slice(0, len).map((v, i) => (labels[i] > localTimeStr ? null : v));
      const maskedSell = (sellValues || []).slice(0, len).map((v, i) => (labels[i] > localTimeStr ? null : v));
      const maskedUnreliableBuy = (optionalUnreliableBuy || []).slice(0, len).map((v, i) => (labels[i] > localTimeStr ? null : v));
      const maskedUnreliableSell = (optionalUnreliableSell || []).slice(0, len).map((v, i) => (labels[i] > localTimeStr ? null : v));
      return {
        buyValues: maskedBuy,
        sellValues: maskedSell,
        unreliableBuy: maskedUnreliableBuy,
        unreliableSell: maskedUnreliableSell
      };
    },
    renderLatex(latexString, displayMode = false) {
      try {
        return katex.renderToString(latexString, {
          throwOnError: false,
          displayMode: displayMode
        });
      } catch (error) {
        console.error('Error rendering LaTeX:', error);
        return latexString;
      }
    },
    extractChartData(chartType) {
      let datasets = [];
      let filename = '';
      
      const plantInfo = `PlantId-${this.plantId}_Alias-${this.alias || 'unknown'}_Timezone-${this.timezoneDataMap.timezone_name || 'unknown'}`;
      const safePlantInfo = plantInfo.replace(/[^a-zA-Z0-9_-]/g, '_');
      
      switch (chartType) {
        case 'power':
          datasets = this.getPowerDatasets();
          filename = `Power_Data_${this.selectedDate}_${safePlantInfo}.csv`;
          break;
        case 'price':
          datasets = this.getPriceDatasets();
          filename = `Price_Data_${this.selectedDate}_${safePlantInfo}.csv`;
          break;
        case 'soc':
          datasets = this.chartDataSocActual;
          filename = `SoC_Data_${this.selectedDate}_${safePlantInfo}.csv`;
          break;
        default:
          return;
      }

      let visibleDatasets = [];
      if (chartType === 'power') {
        visibleDatasets = datasets.filter(dataset => 
          !this.powerChartHiddenLegends.includes(dataset.name)
        );
      } else if (chartType === 'price') {
        visibleDatasets = datasets.filter(dataset => 
          !this.priceChartHiddenLegends.includes(dataset.name) &&
          !dataset.name.includes('Last Known')
        );
      } else {
        visibleDatasets = datasets;
      }

      if (visibleDatasets.length === 0) {
        alert('No visible data to export');
        return;
      }

      let csvContent = 'Time';
      visibleDatasets.forEach(dataset => {
        csvContent += `,${dataset.name}`;
      });
      csvContent += '\n';

      const maxLength = Math.max(...visibleDatasets.map(dataset => dataset.values.length));
      
      for (let i = 0; i < maxLength; i++) {
        const timeLabel = visibleDatasets[0]?.labels?.[i] || `${Math.floor(i/12)}:${String((i*5)%60).padStart(2, '0')}`;
        csvContent += timeLabel;
        
        visibleDatasets.forEach(dataset => {
          const value = dataset.values[i] !== undefined ? dataset.values[i] : '';
          csvContent += `,${value}`;
        });
        csvContent += '\n';
      }

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  },
  created() {
    // Initialize state for the selected range from URL
    this.initializeRangeState(this.selectedDateRange);
    // Only auto-fetch for daily tab, other tabs require button click
    if (this.selectedDateRange === 'daily') {
      this.hasFetchedData = true; // Daily auto-fetches, so mark as fetched
      this.triggerAllFetches();
    } else {
      this.hasFetchedData = false; // Other tabs require button click
    }
    this.fetchDataAvailability();
    eventBus.on('environment-changed', this.handleEnvironmentChange);
  },
  beforeUnmount() {
    eventBus.off('environment-changed', this.handleEnvironmentChange);
  },
  watch: {
    selectedDate(newDate, oldDate) {
      if (newDate !== oldDate && this.selectedDateRange === 'daily') {
        this.triggerAllFetches();
      } else if (newDate !== oldDate && (this.selectedDateRange === 'weekly' || this.selectedDateRange === 'monthly')) {
        // Update rangeStartDate when selectedDate changes for weekly/monthly
        this.rangeStartDate = newDate;
        // Reset fetch flag when date changes
        this.hasFetchedData = false;
        // Don't auto-fetch, wait for button click
      }
    },
    rangeStartDate(newDate, oldDate) {
      // Don't auto-fetch, wait for button click
    },
    '$route.query.range'(newRange) {
      // Sync selectedDateRange when URL query parameter changes externally
      if (newRange && ['daily', 'weekly', 'monthly', 'custom'].includes(newRange)) {
        if (this.selectedDateRange !== newRange) {
          this.selectedDateRange = newRange;
          this.initializeRangeState(newRange);
          // Reset fetch flag when switching to non-daily ranges
          if (newRange !== 'daily') {
            this.hasFetchedData = false;
          }
          // Only auto-fetch for daily tab
          if (newRange === 'daily') {
            this.hasFetchedData = true; // Daily auto-fetches, so mark as fetched
            this.triggerAllFetches();
          }
        }
      } else if (!newRange && this.selectedDateRange !== 'daily') {
        // If range is removed from URL, default to daily
        this.selectedDateRange = 'daily';
        this.updateUrlRange('daily');
        this.hasFetchedData = true; // Daily auto-fetches, so mark as fetched
        this.triggerAllFetches();
      }
    },
    'userPreferences.showDeviceSoHComparison'(newVal) {
      if (newVal && this.plantDevicesDataMap.length > 0) {
        this.plantDevicesDataMap.forEach((device) => {
          if (device.device_sn) {
            this.fetchDeviceSoHComparison(device.device_sn);
          }
        });
      }
    }
  }
};
</script>

<style scoped>
.birdseye-page-container {
  padding: 20px;
}

/* Professional Tab Design */
.date-range-selector-wrapper {
  margin: 1.5rem 0;
}

.date-range-tabs-container {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
  overflow: hidden;
  border: 1px solid var(--border-color);
}

.tabs-header {
  display: flex;
  background: var(--bg-secondary);
  border-bottom: 2px solid var(--border-color);
  position: relative;
  padding: 0;
  margin: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.tabs-header::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.tab-button {
  flex: 1;
  min-width: 100px;
  padding: 14px 20px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  color: var(--text-secondary);
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  white-space: nowrap;
  text-align: center;
  outline: none;
}

.tab-button:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.tab-button.active {
  color: var(--custom-color1, #667eea);
  border-bottom-color: var(--custom-color1, #667eea);
  background: var(--card-bg);
  font-weight: 600;
  position: relative;
}

.tab-button.active::before {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--custom-color1, #667eea);
  border-radius: 3px 3px 0 0;
  z-index: 2;
}

.tab-button.active::after {
  content: '';
  position: absolute;
  bottom: -3px;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--card-bg);
  z-index: 1;
}

.tab-content-panel {
  padding: 1.5rem 2rem;
  background: var(--card-bg);
  border-top: 1px solid var(--border-color);
  min-height: 80px;
  position: relative;
  animation: fadeInContent 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  align-items: center;
}

@keyframes fadeInContent {
  from {
    opacity: 0.7;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.custom-date-range {
  padding: 0;
}

.date-range-inputs {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.date-input-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
}

.date-input-group label {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
}

.date-input-group input {
  padding: 8px 12px;
  border: 2px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.9rem;
  background: var(--card-bg);
  color: var(--text-primary);
}

.date-input-group input:focus {
  outline: none;
  border-color: var(--custom-color1);
}

.selected-date-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
}

.range-navigation {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--bg-secondary);
  border-radius: 8px;
}

.nav-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 16px;
  border: 2px solid var(--border-color);
  background: var(--card-bg);
  color: var(--text-secondary);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  font-weight: 500;
}

.nav-button:hover:not(:disabled) {
  border-color: var(--custom-color1);
  background: var(--custom-color1);
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.nav-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: var(--bg-secondary);
}

.nav-button svg {
  width: 16px;
  height: 16px;
}

.range-display {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  min-width: 200px;
  text-align: center;
}

.range-info-text {
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: #666;
  text-align: center;
  font-style: italic;
}

/* Monthly Options Styles */
.monthly-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  background: var(--bg-secondary);
  border-radius: 8px;
  margin-bottom: 1rem;
}

.monthly-toggle-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.monthly-toggle-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
}

.monthly-toggle-switch {
  display: inline-flex;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 2px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.monthly-toggle-btn {
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 500;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  white-space: nowrap;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}

.monthly-toggle-btn:not(:first-child) {
  margin-left: -1px;
}

.monthly-toggle-btn:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}

.monthly-toggle-btn:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}

.monthly-toggle-btn:hover:not(.active) {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  z-index: 1;
}

.monthly-toggle-btn.active {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  border-color: #4CAF50;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
  z-index: 2;
  font-weight: 600;
}

.monthly-toggle-btn.active:hover {
  background: linear-gradient(135deg, #45a049 0%, #3d8b40 100%);
  box-shadow: 0 3px 10px rgba(76, 175, 80, 0.4);
}

.monthly-toggle-btn:focus {
  outline: none;
}

.monthly-toggle-btn:focus-visible {
  outline: 2px solid #4CAF50;
  outline-offset: 2px;
}

/* Month/Year Picker Styles */
.month-year-picker {
  display: flex;
  justify-content: center;
  padding-top: 0.5rem;
  border-top: 1px solid var(--border-color);
}

.month-year-inputs {
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex-wrap: wrap;
  justify-content: center;
}

.month-year-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  min-width: 150px;
}

.month-year-group label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary);
}

.month-select,
.year-select {
  padding: 8px 12px;
  border: 2px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.9rem;
  background: var(--card-bg);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.3s ease;
}

.month-select:focus,
.year-select:focus {
  outline: none;
  border-color: var(--custom-color1, #667eea);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.month-select:hover,
.year-select:hover {
  border-color: var(--custom-color1, #667eea);
}

/* Fetch Data Button Styles */
.fetch-data-button-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem 0;
  margin-top: 1rem;
}

.fetch-data-button {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  min-width: 150px;
  justify-content: center;
}

.fetch-data-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

.fetch-data-button:active:not(:disabled) {
  transform: translateY(0);
}

.fetch-data-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: var(--bg-secondary);
  color: var(--text-tertiary);
  box-shadow: none;
}

.fetch-data-button svg {
  width: 18px;
  height: 18px;
}

@media (max-width: 750px) {
  .date-range-tabs-container {
    border-radius: 8px;
  }
  
  .tabs-header {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  .tab-button {
    min-width: 80px;
    padding: 12px 16px;
    font-size: 0.85rem;
    flex-shrink: 0;
  }
  
  .tab-content-panel {
    padding: 1rem;
    align-items: stretch;
  }
  
  .range-navigation {
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.75rem;
  }
  
  .nav-button {
    width: 100%;
    justify-content: center;
  }
  
  .range-display {
    min-width: auto;
  }
  
  .date-range-inputs {
    flex-direction: column;
  }
  
  .date-input-group {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .monthly-options {
    padding: 0.75rem;
  }
  
  .monthly-toggle-container {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
  
  .monthly-toggle-label {
    text-align: center;
  }
  
  .monthly-toggle-switch {
    width: 100%;
    display: flex;
  }
  
  .monthly-toggle-btn {
    flex: 1;
    padding: 10px 8px;
    font-size: 0.8rem;
    min-height: 44px;
  }
  
  .month-year-inputs {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .month-year-group {
    width: 100%;
    min-width: auto;
    flex-direction: column;
    align-items: flex-start;
  }
  
  .fetch-data-button {
    width: 100%;
    min-width: auto;
  }
}

/* Extra Info Container Styles */
/* Plant Information Card Styles */
.plant-info-card {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 12px var(--shadow-color);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid var(--border-color);
}

.plant-info-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-color);
}

.plant-info-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-primary);
}

.plant-info-title svg {
  width: 24px;
  height: 24px;
  color: #667eea;
}

.plant-info-title h4 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
}

.plant-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.plant-details-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-label svg {
  width: 16px;
  height: 16px;
  color: #667eea;
}

.info-value {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  padding: 0.5rem 0.75rem;
  background: var(--bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
  border-left: 3px solid #667eea;
}

/* Quick Actions Card Styles */
.quick-actions-card {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 12px var(--shadow-color);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid var(--border-color);
}

.data-availability-card .info-value {
  font-size: 1rem;
}

.data-availability-status {
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
}

.data-availability-status.error {
  color: #c53030;
  border-color: #feb2b2;
}


.quick-actions-header {
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-color);
}

.quick-actions-header h4 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
}

.quick-actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: var(--card-bg);
  border: 2px solid var(--border-color);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;
  width: 100%;
}

.action-button:hover {
  border-color: #667eea;
  background: var(--bg-secondary);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
}

.action-button.active {
  border-color: #667eea;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
}

.action-button.active .action-icon svg {
  color: white;
}

.action-button.active .action-title {
  color: white;
}

.action-button.active .action-status {
  color: rgba(255, 255, 255, 0.9);
}

.action-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-secondary);
  border-radius: 10px;
  flex-shrink: 0;
}

.action-icon svg {
  width: 20px;
  height: 20px;
  color: #667eea;
}

.action-button.active .action-icon {
  background: rgba(255, 255, 255, 0.2);
}

.action-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.action-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
}

.action-status {
  font-size: 0.8rem;
  color: var(--text-tertiary);
  font-weight: 500;
}

/* User Preferences Styles */
.user-preferences-container {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
  padding: 1.5rem;
  margin: 1rem 0;
}

.user-preferences-container h6 {
  margin: 0 0 1rem 0;
  text-align: center;
  color: var(--text-primary);
  font-weight: 600;
}

.preferences-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.preference-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background-color: var(--bg-secondary);
  transition: all 0.2s ease;
}

.preference-item:hover {
  background-color: var(--bg-tertiary);
  border-color: var(--border-color);
}

.preference-checkbox {
  width: 18px;
  height: 18px;
  margin-right: 0.75rem;
  cursor: pointer;
  accent-color: var(--custom-color1, #4CAF50);
  background-color: var(--input-bg);
  border: 2px solid var(--border-color);
}

.preference-label {
  font-size: 0.9rem;
  color: var(--text-secondary);
  cursor: pointer;
  user-select: none;
  flex: 1;
  line-height: 1.4;
}

.preferences-actions {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

.reset-button {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%);
  color: white;
  padding: 8px 16px;
  font-size: 0.9rem;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.reset-button:hover {
  background: linear-gradient(135deg, #ff5252 0%, #e53935 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

/* Plant Devices Card Styles */
.plant-devices-card {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 12px var(--shadow-color);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid var(--border-color);
}

.devices-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-color);
}

.devices-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-secondary);
}

.devices-title svg {
  width: 24px;
  height: 24px;
  color: #667eea;
}

.devices-title h4 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
}

.devices-count-badge {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 20px;
  font-weight: 600;
}

.devices-count-badge span:first-child {
  font-size: 1.5rem;
  font-weight: 700;
}

.devices-count-badge .count-label {
  font-size: 0.85rem;
  font-weight: 500;
  opacity: 0.9;
}

.no-devices-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  color: var(--text-tertiary);
}

.no-devices-message svg {
  width: 64px;
  height: 64px;
  margin-bottom: 1rem;
  color: var(--text-tertiary);
}

.no-devices-message p {
  margin: 0;
  font-size: 1rem;
  color: var(--text-tertiary);
}

.devices-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.device-card {
  background: var(--card-bg);
  border: 2px solid var(--border-color);
  border-radius: 12px;
  padding: 1.25rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.device-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
}

.device-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.15);
  border-color: #667eea;
}

.device-card-header {
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--border-color);
}

.device-number {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.device-number svg {
  width: 20px;
  height: 20px;
  color: #667eea;
}

.device-card-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.device-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.device-field-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.device-field-label svg {
  width: 14px;
  height: 14px;
  color: #667eea;
}

.device-sn-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--card-bg);
  border: 2px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  word-break: break-all;
}

.device-sn-link svg {
  width: 16px;
  height: 16px;
  color: #667eea;
  flex-shrink: 0;
}

.device-sn-link:hover {
  border-color: #667eea;
  background: var(--bg-secondary);
  color: #667eea;
  transform: translateX(4px);
}

.device-type-badge {
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  text-align: center;
}

.device-soh-comparison {
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border-color, #e2e8f0);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.device-soh-comparison .soh-comparison-title {
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--text-secondary, #64748b);
}

.device-soh-loading {
  font-size: 0.85rem;
  color: var(--text-secondary, #64748b);
  font-style: italic;
}

.device-soh-values {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  font-size: 0.9rem;
}

@media (max-width: 750px) {
  .devices-grid {
    grid-template-columns: 1fr;
  }

  .devices-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .device-card {
    padding: 1rem;
  }
}


@media (max-width: 750px) {
  .preferences-grid {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  .preference-item {
    padding: 0.5rem;
  }
  
  .preference-label {
    font-size: 0.8rem;
  }
  
  .preference-checkbox {
    width: 16px;
    height: 16px;
    margin-right: 0.5rem;
  }
}

/* Reuse styles from DetailPage */
/* Plant Devices Container (legacy support) */
.info-container {
  margin: 1rem 0;
}

/* Responsive styles for plant info and quick actions */
@media (max-width: 750px) {
  .plant-info-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .quick-actions-grid {
    grid-template-columns: 1fr;
  }

  .action-button {
    padding: 0.875rem 1rem;
  }

  .plant-info-card,
  .quick-actions-card {
    padding: 1.25rem;
  }
}

.chart-container {
  display: block;
  text-align: center;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
  padding: 0rem 1rem 3rem 1rem;
}

.chart-container > h5 {
  margin: 0.2rem;
}

.extra-details-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 30px;
  padding: 10px;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  background: var(--card-bg);
}

.extract-data-container {
  display: flex;
  justify-content: flex-end;
  margin: 10px 0;
}

/* Toggle Group Styles */
.toggle-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
}

.toggle-group-label {
  font-weight: 600;
  font-size: 0.75rem;
  color: var(--text-primary);
  margin: 0;
  padding: 0;
  text-transform: none;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.toggle-buttons {
  display: inline-flex;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 2px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.toggle-btn {
  padding: 8px 16px;
  font-size: 0.75rem;
  font-weight: 500;
  border: none;
  background: transparent;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  white-space: nowrap;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-btn:not(:first-child) {
  margin-left: -1px;
}

.toggle-btn:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}

.toggle-btn:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}

.toggle-btn:hover:not(.active) {
  background: #f5f5f5;
  color: #333;
  z-index: 1;
}

.toggle-btn.active {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  border-color: #4CAF50;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
  z-index: 2;
  font-weight: 600;
}

.toggle-btn.active:hover {
  background: linear-gradient(135deg, #45a049 0%, #3d8b40 100%);
  box-shadow: 0 3px 10px rgba(76, 175, 80, 0.4);
}

.toggle-btn:focus {
  outline: none;
}

.toggle-btn:focus-visible {
  outline: 2px solid #4CAF50;
  outline-offset: 2px;
}

.display-text-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  background-color: #f9f9f9;
  color: red;
}

.custom-divider {
  margin: 2rem 0;
  border-top: 1px solid #ddd;
}

/* Performance Metrics Styles */
.performance-metrics-container {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 2rem;
  margin: 2rem 0;
  box-shadow: 0 4px 20px var(--shadow-color);
}

.metrics-header {
  text-align: center;
  margin-bottom: 2rem;
}

.metrics-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.metrics-subtitle {
  font-size: 0.95rem;
  color: var(--text-tertiary);
  margin: 0;
  font-weight: 400;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  justify-items: stretch;
}

.metric-card {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 12px var(--shadow-color);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  border: 1px solid var(--border-color);
}

.metric-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #e0e0e0 0%, #f0f0f0 100%);
  transition: all 0.3s ease;
}

.metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.metric-card:hover::before {
  height: 4px;
}

/* Featured Savings Card */
.featured-card {
  grid-column: span 2;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  text-align: center;
}

.featured-card::before {
  display: none;
}

.featured-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.3);
}

.savings-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.savings-card .metric-icon {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  margin-left: auto;
  margin-right: auto;
}

.savings-card .metric-content {
  text-align: center;
  width: 100%;
}

.savings-card .metric-label {
  color: rgba(255, 255, 255, 0.9);
}

.savings-card .metric-value {
  color: white;
  font-size: 2.5rem;
}

.savings-card .metric-description {
  color: rgba(255, 255, 255, 0.8);
}

/* Card-specific colors */
.consumption-card::before {
  background: linear-gradient(90deg, #4CAF50 0%, #45a049 100%);
}

.grid-import-card::before {
  background: linear-gradient(90deg, #3F51B5 0%, #303F9F 100%);
}

.net-cost-card::before {
  background: linear-gradient(90deg, #2196F3 0%, #1976D2 100%);
}

.usage-card::before {
  background: linear-gradient(90deg, #FF9800 0%, #F57C00 100%);
}

.feedin-card::before {
  background: linear-gradient(90deg, #9C27B0 0%, #7B1FA2 100%);
}

.original-cost-card::before {
  background: linear-gradient(90deg, #F44336 0%, #D32F2F 100%);
}

.optimised-cost-card::before {
  background: linear-gradient(90deg, #00BCD4 0%, #0097A7 100%);
}

.original-cost-perkwh-card::before {
  background: linear-gradient(90deg, #E91E63 0%, #C2185B 100%);
}

.optimised-cost-perkwh-card::before {
  background: linear-gradient(90deg, #26A69A 0%, #00897B 100%);
}

.metric-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: #667eea;
}

.metric-icon svg {
  width: 24px;
  height: 24px;
}

.metric-content {
  flex: 1;
}

.metric-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
}

.metric-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
  line-height: 1.2;
}

.metric-unit {
  font-size: 1rem;
  font-weight: 500;
  color: var(--text-tertiary);
}

.metric-subvalue {
  font-size: 0.9rem;
  font-weight: 500;
  color: #667eea;
  margin-bottom: 0.5rem;
}

.metric-description {
  font-size: 0.8rem;
  color: var(--text-tertiary);
  margin-top: 0.5rem;
  line-height: 1.4;
}

.metrics-footer {
  text-align: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid var(--border-color);
}

.report-button {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.report-button svg {
  width: 18px;
  height: 18px;
}

.report-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

.report-button:active {
  transform: translateY(0);
}

@media (max-width: 1200px) {
  .featured-card {
    grid-column: span 1;
  }
}

@media (max-width: 750px) {
  .performance-metrics-container {
    padding: 1.5rem;
    margin: 1rem 0;
  }

  .metrics-title {
    font-size: 1.5rem;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .featured-card {
    grid-column: span 1;
  }

  .metric-card {
    padding: 1.25rem;
  }

  .metric-value {
    font-size: 1.5rem;
  }

  .report-button {
    width: 100%;
    justify-content: center;
  }
}

.custom-button {
  cursor: pointer;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  border-radius: 5px;
  font-size: 0.75rem;
  padding: 8px 16px;
  transition: all 0.3s ease;
}

.custom-button:hover {
  box-shadow: 0 2px 6px rgba(60,64,67,0.25);
  background: var(--custom-color1);
  color: white;
}

.single-button-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.bottom-pillar {
  height: 10rem;
}

.bms-simulator-wrapper {
  border: 3px solid #ff8c00;
  border-radius: 12px;
  margin: 1rem 0;
  padding: 0;
  background: var(--card-bg);
  box-shadow: 0 4px 12px rgba(255, 140, 0, 0.2);
}

.accuracy-eval-box {
  margin: 2rem 0;
}

@media (max-width: 750px) {
  .extra-details-box {
    flex-direction: column;
  }
  
  .centered-flex-box {
    flex-direction: column;
  }
  
  /* Toggle Button Responsive Styles */
  .toggle-group {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
  }
  
  .toggle-group-label {
    font-size: 0.75rem;
    margin-bottom: 0.5rem;
  }
  
  .toggle-buttons {
    width: 100%;
    display: flex;
  }
  
  .toggle-btn {
    padding: 10px 8px;
    font-size: 0.75rem;
    min-height: 44px; /* Ensure tappable target size */
    flex: 1;
    min-width: 0;
  }
}

/* Formula Section Styles */
.formula-section {
  margin: 1.5rem 0;
  border-top: 1px solid var(--border-color);
  padding-top: 1.5rem;
}

.formula-toggle-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: none;
  color: var(--text-tertiary);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem 0;
  transition: color 0.2s ease;
  width: 100%;
  text-align: left;
}

.formula-toggle-btn:hover {
  color: var(--custom-color1, #4CAF50);
}

.toggle-icon {
  font-size: 0.75rem;
  transition: transform 0.2s ease;
}

.formula-content {
  margin-top: 1rem;
  padding: 1rem;
  background: var(--bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.formula-box {
  background: var(--card-bg);
  padding: 1.25rem;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  margin-bottom: 1rem;
}

.formula-box:last-child {
  margin-bottom: 0;
}

.formula-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border-color);
}

.formula-expression {
  font-size: 1rem;
  color: var(--text-primary);
  padding: 1rem;
  background: var(--bg-secondary);
  border-radius: 6px;
  overflow-x: auto;
  margin: 0.75rem 0;
  text-align: center;
}

.formula-expression .katex {
  font-size: 1.1rem;
}

.formula-description {
  font-size: 0.85rem;
  line-height: 1.6;
  color: var(--text-secondary);
  margin: 0.75rem 0;
}

.formula-variables {
  margin-top: 0.75rem;
  font-size: 0.85rem;
  line-height: 1.6;
  color: var(--text-secondary);
}

.formula-variables p {
  margin: 0.5rem 0;
  font-weight: 600;
  color: var(--text-primary);
}

.formula-variables ul {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
  list-style-type: disc;
}

.formula-variables li {
  margin: 0.5rem 0;
  line-height: 1.6;
}

.formula-nested {
  margin: 0.5rem 0 !important;
  padding: 0.75rem;
  background: var(--bg-secondary);
  border-radius: 4px;
  text-align: center;
  list-style: none !important;
  padding-left: 0 !important;
}

.formula-nested .katex {
  font-size: 1rem;
}

/* Formula expand animation */
.formula-expand-enter-active,
.formula-expand-leave-active {
  transition: all 0.3s ease;
  max-height: 3000px;
  overflow: hidden;
}

.formula-expand-enter-from,
.formula-expand-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
}

@media (max-width: 750px) {
  .formula-box {
    padding: 1rem;
  }
  
  .formula-expression {
    padding: 0.75rem;
    font-size: 0.9rem;
  }
  
  .formula-expression .katex {
    font-size: 0.95rem;
  }
  
  .formula-nested .katex {
    font-size: 0.9rem;
  }
  
  .formula-variables ul {
    padding-left: 1.25rem;
  }
}
</style>

